/**
 * Distribution, Brokers & Logistics — 7 templates.
 *
 * The paper that governs everyone who moves, sells or holds the product between the co-packer
 * and the shelf: beverage and food distributors, a deliberately non-exclusive default, brokers
 * and commission reps, and the two buildings a brand's inventory sits in.
 */

export const DISTRIBUTION_TEMPLATES = [
  // ───────────────────────────────────────────────────────────────────────────────────────
  // 1. BEVERAGE DISTRIBUTOR AGREEMENT
  // ───────────────────────────────────────────────────────────────────────────────────────
  {
    id: "beverage-distributor-agreement",
    docId: "beverage-distributor-agreement",
    title: "BEVERAGE DISTRIBUTOR AGREEMENT",
    version: "1.0",
    lastReviewed: "2026-08-25",

    founderGuide: {
      useThisWhen:
        "A wholesaler is ready to buy your product and call on accounts in your name, and you are about to ship the first pallet. Use it as your opening paper, and as the checklist you read their form against — most distributors will hand you their own.",
      whyItMatters:
        "This is the agreement most likely to cap what your brand is worth. An exclusive territory with no minimums and no clean exit can freeze a region for years while producing almost no volume, and you cannot appoint anyone else there while it runs. It also travels: an acquirer discounts for a distributor agreement they inherit and cannot end. How hard a beverage distribution agreement is to exit varies by state and turns on exactly what the document says, so this is one to take to counsel in the relevant state before you sign, not after.",
      negotiateCarefully: [
        {
          point: "Exclusivity has to be earned and has to be losable",
          detail:
            "If you grant exclusivity at all, condition it in the operative clause on stated purchase and depletion minimums and on account coverage, with automatic conversion to non-exclusive or territory reduction when they are missed. A volume table in an appendix that the grant never references is a forecast, not a condition.",
        },
        {
          point: "Carve out the business you already generate",
          detail:
            "Your own site, subscription, marketplace listings, the chains you service directly, and military and government accounts should be reserved by name before the grant is written, not argued about after the first invoice. Otherwise you can end up paying distributor margin on demand you created and fulfilled yourself.",
        },
        {
          point: "Two exits, not one",
          detail:
            "Termination for uncured breach is slow and contested, and the territory sits idle while you argue. Add termination for convenience on notice, and a separate, faster trigger tied only to missing minimums so you never have to prove anything about their conduct.",
        },
        {
          point: "Size the repurchase before you sign it",
          detail:
            "A buy-back obligation covering all remaining inventory at their landed cost turns leaving into a cash event. Make repurchase your option rather than your obligation, price it at what they paid net of credits already taken, and exclude aged, out-of-code, damaged, opened and obsolete-packaging stock.",
        },
        {
          point: "Keep them out of your exit",
          detail:
            "Assignment and change-of-control language that lets a distributor consent to, block or be paid on a sale of your brand puts them at your acquisition table. Make your side assignable to a successor without consent and give them no payment right on a change of control of the brand.",
        },
      ],
      redFlags: [
        "Exclusivity granted with no purchase or depletion minimum stated as a condition of keeping it.",
        "No termination right except for uncured material breach, or a right only they may use.",
        "A grant covering all sales in the territory with no carve-out for your own site, marketplaces or house accounts.",
        "An obligation on you to repurchase all inventory at their cost regardless of age or condition.",
        "Distributor consent or a payment required on a sale of your company, brand or assets.",
        "Open-ended spoils, damage and promotional deductions taken off the remittance rather than claimed.",
      ],
      timeToComplete: "90 minutes to fill · fill the schedules first, then have counsel in the territory's state read it",
    },

    placeholders: [
      { token: "[EFFECTIVE DATE]", label: "Effective date", example: "September 1, 2026" },
      { token: "[COMPANY NAME]", label: "Your legal entity name", brandKey: "legalEntityName", example: "Acme Beverage Co., LLC" },
      { token: "[COMPANY STATE]", label: "Your state of formation", brandKey: "stateOfFormation", example: "Delaware" },
      { token: "[COMPANY ENTITY TYPE]", label: "Your entity type", example: "limited liability company" },
      { token: "[COMPANY ADDRESS]", label: "Your address", brandKey: "principalAddress", example: "123 Main St, Austin, TX 78701" },
      { token: "[BRAND NAME]", label: "Your brand name", brandKey: "tradeName", example: "Acme" },
      { token: "[PRODUCT CATEGORY]", label: "Product category", brandKey: "productCategory", example: "functional beverage" },
      { token: "[DISTRIBUTOR NAME]", label: "Distributor's legal name", example: "Gulf Coast Beverage Wholesale, Inc." },
      { token: "[DISTRIBUTOR STATE]", label: "Distributor's state of formation", example: "Texas" },
      { token: "[DISTRIBUTOR ENTITY TYPE]", label: "Distributor's entity type", example: "corporation" },
      { token: "[DISTRIBUTOR ADDRESS]", label: "Distributor's address", example: "4400 Depot Rd, Houston, TX 77029" },
      { token: "[APPOINTMENT TYPE]", label: "Exclusive or non-exclusive appointment", example: "non-exclusive" },
      { token: "[CHANNELS COVERED]", label: "Channels the appointment covers", example: "off-premise retail and convenience only" },
      { token: "[EXCLUDED CHANNELS]", label: "Channels expressly excluded", example: "on-premise, foodservice and club" },
      { token: "[ORDER LEAD TIME]", label: "Lead time for accepted orders", example: "10 business days" },
      { token: "[MINIMUM ORDER]", label: "Minimum order quantity", example: "one full pallet layer per SKU, 20 pallets per truckload" },
      { token: "[PRICE CHANGE NOTICE]", label: "Notice before a price change takes effect", example: "60 days" },
      { token: "[PRICE PROTECTION WINDOW]", label: "How long orders may be placed at the old price", example: "15 days after the notice, up to one normal order cycle" },
      { token: "[PAYMENT TERMS]", label: "Payment terms", example: "net 30 days from invoice date" },
      { token: "[CREDIT LIMIT]", label: "Credit limit", example: "$50,000 outstanding at any time" },
      { token: "[MEASUREMENT PERIOD]", label: "Period minimums are measured over", example: "each calendar quarter" },
      { token: "[MINIMUM PURCHASE VOLUME]", label: "Minimum purchase per measurement period", example: "1,200 cases per quarter" },
      { token: "[MINIMUM DEPLETION VOLUME]", label: "Minimum depletions per measurement period", example: "1,000 cases shipped to accounts per quarter" },
      { token: "[SHORTFALL PERIODS]", label: "Consecutive missed periods that trigger the remedy", example: "two" },
      { token: "[SHORTFALL NOTICE]", label: "Notice to act on a shortfall", example: "30 days" },
      { token: "[ACCOUNT COVERAGE TARGET]", label: "Account coverage commitment", example: "150 active points of distribution by the end of the first year" },
      { token: "[CALL FREQUENCY]", label: "How often listed accounts are called on", example: "every two weeks for chain accounts, monthly for independents" },
      { token: "[REPORTING CADENCE]", label: "Reporting cadence", example: "monthly, by the tenth business day of the following month" },
      { token: "[REPORTING FORMAT]", label: "Format the reports arrive in", example: "a spreadsheet in Brand's standard template" },
      { token: "[FREIGHT TERMS]", label: "Freight and delivery terms", example: "FOB Brand's warehouse, freight prepaid and added below full truckload" },
      { token: "[STORAGE CONDITIONS]", label: "Storage and transport conditions", example: "dry, between 45 and 80 degrees Fahrenheit, away from direct sunlight" },
      { token: "[FREE FILL CAP]", label: "Cap on free fill product", example: "one case per new account, up to 40 cases per quarter" },
      { token: "[SAMPLE CAP]", label: "Cap on samples and demo product", example: "20 cases per quarter" },
      { token: "[PROMOTION APPROVAL PERIOD]", label: "Time for the brand to approve promotional support", example: "10 business days" },
      { token: "[SPOILS ALLOWANCE]", label: "Spoils and unsaleable allowance", example: "1% of purchases in the measurement period" },
      { token: "[SPOILS CLAIM PERIOD]", label: "Window to submit a spoils claim", example: "45 days from the date the product became unsaleable" },
      { token: "[RETURN AUTHORIZATION PERIOD]", label: "Time to act on a return authorisation", example: "30 days from issue" },
      { token: "[CLAIM RESPONSE PERIOD]", label: "Time for the brand to respond to a claim", example: "15 business days" },
      { token: "[DEDUCTION DISPUTE PERIOD]", label: "Window to dispute a deduction", example: "90 days" },
      { token: "[MARKETING COMMITMENT]", label: "What the distributor commits to spend or do", example: "four ad features and two chain promotions per year" },
      { token: "[INITIAL TERM]", label: "Initial term", example: "one year" },
      { token: "[RENEWAL TERM]", label: "Renewal term", example: "one year" },
      { token: "[NON-RENEWAL NOTICE]", label: "Notice of non-renewal", example: "60 days" },
      { token: "[CURE PERIOD]", label: "Cure period for a material breach", example: "30 days" },
      { token: "[CONVENIENCE TERMINATION NOTICE]", label: "Notice to terminate for convenience", example: "90 days" },
      { token: "[REPURCHASE PRICE BASIS]", label: "Price basis for repurchased inventory", example: "the price Distributor paid, net of every allowance, credit and discount already taken" },
      { token: "[REPURCHASE SHELF LIFE MINIMUM]", label: "Minimum remaining shelf life for repurchase", example: "at least 60% of stated shelf life remaining" },
      { token: "[REPURCHASE FREIGHT]", label: "Who pays freight on repurchased inventory", example: "Brand pays outbound freight from Distributor's warehouse" },
      { token: "[SELL-OFF PERIOD]", label: "Post-termination sell-off period", example: "60 days" },
      { token: "[TRANSITION PERIOD]", label: "Transition cooperation period", example: "90 days after termination" },
      { token: "[GENERAL LIABILITY LIMIT]", label: "Commercial general liability limit", example: "$1,000,000 per occurrence" },
      { token: "[PRODUCT LIABILITY LIMIT]", label: "Product liability limit", example: "$2,000,000 aggregate" },
      { token: "[INSURANCE NOTICE PERIOD]", label: "Notice before insurance lapses or changes", example: "30 days" },
      { token: "[CONFIDENTIALITY YEARS]", label: "Years confidentiality survives", example: "5" },
      { token: "[GOVERNING LAW STATE]", label: "Governing law state", example: "Delaware" },
      { token: "[VENUE]", label: "Venue for disputes", example: "Wilmington, Delaware" },
    ],

    body: `This Distribution Agreement (this "Agreement") is entered into as of [EFFECTIVE DATE] (the
"Effective Date") by and between:

  [COMPANY NAME], a [COMPANY STATE] [COMPANY ENTITY TYPE], with a principal place of business at
  [COMPANY ADDRESS] ("Brand"); and

  [DISTRIBUTOR NAME], a [DISTRIBUTOR STATE] [DISTRIBUTOR ENTITY TYPE], with a principal place of
  business at [DISTRIBUTOR ADDRESS] ("Distributor").

Brand produces and sells [PRODUCT CATEGORY] products under the [BRAND NAME] brand. Distributor
buys products for its own account and resells them to trade accounts. The parties agree as
follows.

1. APPOINTMENT
1.1 Brand appoints Distributor as an [APPOINTMENT TYPE] distributor of the Products in the
Channels within the Territory, and Distributor accepts the appointment. Distributor buys the
Products for its own account, takes title on the terms of Section 7, and resells them in its own
name.
1.2 The appointment extends only to the Products listed in Schedule B, only to the Channels
described in Section 3, and only within the Territory described in Schedule A. It gives
Distributor no right to any other product of Brand, no right in any other channel or territory,
and no authority to represent, bind or contract for Brand for any purpose.
1.3 Distributor shall not appoint a sub-distributor, transship the Products outside the
Territory, or sell the Products to a buyer Distributor knows or reasonably should know intends
to resell them outside the Territory or outside the Channels, without Brand's prior written
consent.
1.4 The parties are independent contractors. Nothing in this Agreement creates a partnership,
joint venture, franchise, employment or agency relationship between them.

2. TERRITORY
2.1 "Territory" means the counties, parishes, metropolitan areas and other geography named in
Schedule A. The Territory is what Schedule A names. No map, route list, verbal description or
customer list exchanged in discussion enlarges it.
2.2 Where the appointment is exclusive, Brand shall not during the Term appoint another
distributor for the Products in the Channels within the Territory. Exclusivity is granted in
consideration of, and is conditioned on, Distributor meeting the minimums in Section 8 and the
coverage commitments in Section 9, and is subject at all times to Brand's reserved rights in
Section 4.
2.3 Schedule A may be amended by written agreement. In addition, if Distributor fails to meet
either minimum in Section 8 or the coverage commitment in Section 9 for [SHORTFALL PERIODS]
consecutive Measurement Periods, Brand may on [SHORTFALL NOTICE] written notice convert the
appointment to non-exclusive, remove any county or metropolitan area from Schedule A, or
terminate under Section 20.

3. CHANNELS
3.1 "Channels" means [CHANNELS COVERED]. For the purposes of this Agreement, off-premise means
accounts that sell the Products for consumption away from the account's premises; on-premise
means accounts that sell the Products for consumption on the account's premises; and foodservice
means operators that prepare and serve food and beverages, including institutional, contract
feeding, education, healthcare and travel accounts.
3.2 The appointment does not extend to [EXCLUDED CHANNELS], and Distributor shall not solicit or
sell the Products into an excluded Channel without Brand's prior written consent.

4. RESERVED RIGHTS AND CARVE-OUTS
4.1 Brand reserves for itself and for anyone Brand appoints, and the appointment does not cover,
each of the following, whether or not the sale is delivered to an address inside the Territory:
(a) sales through Brand's own website, subscription programme and any other direct-to-consumer
    channel Brand operates;
(b) sales through any e-commerce site, online retailer or third-party marketplace, and the
    fulfilment of those sales;
(c) the house accounts named in Schedule D, together with their affiliates, banners and
    distribution centres;
(d) military, government and institutional accounts;
(e) product supplied for sampling, demonstration, events, media, investors and Brand personnel;
    and
(f) any Channel excluded under Section 3.2.
4.2 No margin, commission, fee, override or other compensation is payable to Distributor on a
sale made under Section 4.1.
4.3 Schedule D may be amended by written agreement. Where Brand elects to service directly an
account Distributor opened and has been servicing, the parties shall agree in writing on the
treatment of Distributor's remaining inventory held for that account before the change takes
effect.

5. PRODUCTS
5.1 "Products" means the items listed in Schedule B, each with its item code, case pack,
configuration and price to Distributor.
5.2 Brand may add a Product to Schedule B on written notice. Brand may discontinue or reformulate
a Product, or change its packaging or case configuration, on written notice; where Brand
discontinues a Product, Brand shall state in the notice how remaining Distributor inventory of
that Product will be handled, which may include repurchase under Section 22.
5.3 Distributor shall not repackage, relabel, over-sticker, decant, alter or tamper with the
Products or their packaging, and shall not sell any Product that has been repackaged or altered.

6. ORDERS, PRICE AND PAYMENT
6.1 Distributor shall order in writing, in quantities of at least [MINIMUM ORDER]. Brand shall
accept or decline an order in writing and shall ship accepted orders within [ORDER LEAD TIME].
An order is binding only when Brand accepts it.
6.2 The price to Distributor for each Product is stated in Schedule B. Brand may change the
prices in Schedule B on [PRICE CHANGE NOTICE] written notice. Orders placed before the notice,
and orders placed within [PRICE PROTECTION WINDOW], are filled at the prior price.
6.3 Payment terms are [PAYMENT TERMS]. Brand may set and adjust a credit limit for Distributor,
initially [CREDIT LIMIT], and may decline or hold shipments while Distributor is over the limit
or has an undisputed invoice past due.
6.4 Distributor shall not set off, deduct or withhold any amount from a Brand invoice except as
Section 16 allows.

7. TITLE, RISK AND HANDLING
7.1 Title and risk of loss pass to Distributor on delivery in accordance with the freight terms
in Section 12. Distributor buys as principal for resale and not as agent for Brand.
7.2 Distributor shall store and transport the Products within [STORAGE CONDITIONS], shall rotate
stock on a first-expired, first-out basis, and shall not ship to an account any Product that has
passed its code date or that carries insufficient remaining shelf life for that account's
receiving requirements.
7.3 Distributor shall maintain lot and code date records sufficient to identify which lots were
shipped to which accounts, shall retain them for the period stated in Section 25, and shall
cooperate promptly and at Brand's request with any trace exercise, mock recall, withdrawal or
recall Brand conducts, including by placing affected stock on hold and identifying the accounts
that received it.

8. MINIMUM PURCHASES AND MINIMUM DEPLETIONS
8.1 "Measurement Period" means [MEASUREMENT PERIOD].
8.2 Distributor shall purchase from Brand at least [MINIMUM PURCHASE VOLUME] in each Measurement
Period.
8.3 Distributor shall in addition deplete at least [MINIMUM DEPLETION VOLUME] in each Measurement
Period. "Deplete" means shipped from Distributor's warehouse to an account for resale, and
excludes product transferred between Distributor's own facilities, product returned, and product
supplied as free fill or samples.
8.4 Purchases and depletions are measured separately. Meeting one does not satisfy the other, and
buying ahead into inventory does not satisfy Section 8.3.
8.5 If Distributor misses either minimum in a Measurement Period, Brand may require a written
recovery plan. If Distributor misses either minimum in [SHORTFALL PERIODS] consecutive
Measurement Periods, Brand may, on [SHORTFALL NOTICE] written notice and without any obligation
to show fault, take any one or more of the following steps: convert the appointment to
non-exclusive; reduce the Territory or the Channels; remove one or more Products from Schedule B;
or terminate this Agreement. Brand's failure to act on one shortfall does not waive its rights on
a later one.

9. ACCOUNT COVERAGE AND SERVICE
9.1 Distributor shall achieve and maintain [ACCOUNT COVERAGE TARGET], measured by the account
list in Schedule C.
9.2 Distributor shall call on the accounts listed in Schedule C at [CALL FREQUENCY], and shall
maintain shelf presence, rotate stock, remove out-of-code product, and place point-of-sale
material supplied by Brand.
9.3 Distributor shall assign a named sales contact for the Products, shall include the Products
in its regular sales meetings and account presentations, and shall present the Products for
authorisation in each applicable category review during the Term.
9.4 Distributor shall notify Brand promptly of any account complaint about product quality,
condition, code date or labelling.

10. REPORTING
10.1 Distributor shall provide Brand, [REPORTING CADENCE] and in [REPORTING FORMAT]:
(a) depletions by Product and by account, with quantity and net price;
(b) on-hand inventory by Product, lot and code date;
(c) new accounts opened and accounts lost in the period, with the reason; and
(d) open and pending authorisations.
10.2 Distributor shall provide a physical inventory count of the Products on Brand's reasonable
request, and shall permit Brand's representatives to visit its facilities and accompany its
representatives on account calls during normal business hours on reasonable notice.
10.3 Reporting failures that continue after written notice are a material breach for the purposes
of Section 19.

11. SUGGESTED PRICING
11.1 Distributor sets its own resale prices. Nothing in this Agreement is an agreement between
the parties about the price at which Distributor resells the Products.
11.2 Brand may from time to time publish suggested retail and wholesale prices, promotional price
points and a written pricing policy adopted and applied by Brand unilaterally. Any such price is
a suggestion and any such policy is Brand's own statement of the terms on which it chooses to do
business. Brand may communicate them to Distributor for planning purposes.
11.3 Whether to adopt a unilateral pricing policy at all, and how it is written and applied, is a
question for Brand's counsel before any policy is issued.

12. FREIGHT AND DELIVERY
12.1 Freight terms are [FREIGHT TERMS]. Brand shall palletise, mark and label shipments to the
receiving standards of the delivery location, including the Product item code, case count, lot
code and production or pack date on each pallet.
12.2 Distributor shall inspect each delivery on receipt for quantity, damage, code date and
condition, and shall note any shortage, damage or discrepancy on the delivery documents and
notify Brand in writing promptly. Concealed damage is handled under Section 14.
12.3 Distributor shall arrange and pay for all freight from its warehouse to its accounts.

13. FREE FILLS, SAMPLES AND PROMOTIONAL SUPPORT
13.1 Brand shall supply free fill product to open new accounts up to [FREE FILL CAP], and sample
and demonstration product up to [SAMPLE CAP], in each case per Measurement Period and in
accordance with Schedule E.
13.2 Any free fill, sample, point-of-sale material, promotional allowance, ad feature, display
support or event funding beyond the caps in Section 13.1 and Schedule E requires Brand's prior
written approval. Distributor shall request approval in writing, identifying the account, the
Product, the quantity or amount, the period, the price point and the expected outcome. Brand
shall approve or decline within [PROMOTION APPROVAL PERIOD].
13.3 Support Brand has not approved in writing in advance is not payable and may not be deducted.
Distributor shall provide proof of performance for approved support, including the accounts, the
dates, the price points and the volume moved, before any credit is issued.
13.4 Coolers, racks, shelving, display fixtures and other equipment Brand supplies or funds remain
Brand's property, shall be used only for the Products, and shall be returned to Brand on
termination in the condition supplied, ordinary wear excepted.

14. SPOILS, OUT-OF-CODE AND DAMAGED PRODUCT
14.1 Brand shall credit Distributor for Product that becomes unsaleable in Distributor's
possession through no fault of Distributor, up to [SPOILS ALLOWANCE] in each Measurement Period.
Unsaleable product beyond that allowance is credited only where Brand has approved the credit in
writing.
14.2 Distributor shall submit each claim within [SPOILS CLAIM PERIOD], with the Product, quantity,
lot and code date, the account if applicable, the reason, and photographs or other evidence of
condition. Brand may inspect the product, or require its return or documented destruction, before
issuing credit.
14.3 Brand is not responsible for product that became unsaleable because of Distributor's storage,
handling, rotation or transport, or because Distributor held it past its code date, or because
Distributor over-ordered against demand.
14.4 Brand shall replace or credit, at Brand's election, Product that does not conform to
specification on delivery, including concealed damage, provided Distributor notifies Brand within
the period stated in Section 12.2 for visible defects and promptly after discovery for concealed
ones.

15. RETURNS
15.1 Distributor may not return Products without a return authorisation issued by Brand in
writing. Brand shall state in the authorisation the Products, the quantities, the destination and
who pays freight, and the authorisation lapses if unused within [RETURN AUTHORIZATION PERIOD].
15.2 Product returned without authorisation, product that has been repackaged or altered, and
product Distributor did not buy from Brand may be refused and is not credited.

16. CHARGEBACKS AND DEDUCTIONS
16.1 Distributor shall submit a written claim for any credit it believes is due, with the
supporting documentation required by Sections 13, 14 and 15. Brand shall accept, reject or
request further documentation within [CLAIM RESPONSE PERIOD].
16.2 Distributor shall not deduct any amount from an invoice before Brand has issued a credit,
except where Section 16.3 applies.
16.3 Where Distributor nevertheless takes a deduction, it shall deliver with the remittance
documentation identifying the invoice deducted against, the event or programme relied on, the
accounts and quantities affected, and the calculation. A deduction delivered without that
documentation is repayable on Brand's demand and may be re-invoiced.
16.4 Brand may dispute a deduction within [DEDUCTION DISPUTE PERIOD] running from the later of
the remittance and Brand's receipt of the documentation. The parties shall escalate an unresolved
dispute to a senior representative of each party before either commences proceedings.
16.5 Neither party may assert a deduction, chargeback or claim relating to an invoice after
[DEDUCTION DISPUTE PERIOD] has run from that invoice, other than a claim of fraud.

17. MARKETING
17.1 Distributor shall, during the Term, [MARKETING COMMITMENT], and shall present its planned
activity for each Measurement Period to Brand in advance.
17.2 Brand shall supply, at Brand's cost unless Schedule E provides otherwise, current sell
sheets, images, point-of-sale material and product information, and shall give Distributor
reasonable notice of new items, packaging changes and national marketing activity affecting the
Territory.
17.3 Neither party shall issue a press release or public statement about the other or about this
Agreement without the other's prior written consent, except that Distributor may identify the
Products in its ordinary trade communications and Brand may identify Distributor as a
distributor.

18. TRADEMARKS
18.1 Brand grants Distributor a non-exclusive, non-transferable, royalty-free licence to use the
[BRAND NAME] marks solely to advertise, promote and resell the Products in the Channels within
the Territory during the Term, in the form and manner Brand approves.
18.2 Distributor shall not use the marks in its own name, trade name, logo, signage, social media
handle or domain name; shall not register or apply to register the marks or anything confusingly
similar anywhere; shall not use the marks in a way that suggests it owns them; and shall not
challenge Brand's ownership of them. All goodwill from Distributor's use runs to Brand.
18.3 Distributor's licence ends on termination, subject only to the sell-off right in Section 23.
18.4 Distributor shall not make, and shall instruct its personnel not to make, any claim about the
Products beyond the claims that appear on the Products' current labelling and on materials Brand
has supplied or approved in writing.

19. TERM AND RENEWAL
19.1 This Agreement begins on the Effective Date and continues for [INITIAL TERM] (the "Initial
Term"), then renews for successive periods of [RENEWAL TERM] unless either party gives written
notice of non-renewal at least [NON-RENEWAL NOTICE] before the end of the then-current term. The
Initial Term and any renewal are the "Term".

20. TERMINATION
20.1 For cause. Either party may terminate on written notice if the other materially breaches and
fails to cure within [CURE PERIOD] after written notice describing the breach and what is
required to cure it. A cure period runs once per breach and does not restart on a repetition of
the same breach.
20.2 Immediately. Either party may terminate immediately on written notice if the other becomes
insolvent, makes an assignment for the benefit of creditors, has a receiver appointed over a
substantial part of its assets, or ceases to carry on business. Brand may also terminate
immediately if Distributor transships the Products outside the Territory or the Channels, sells
Product that has been repackaged or altered, misstates a report or claim in a material respect,
or undergoes a change of control to which Brand objects under Section 26.
20.3 For convenience. Either party may terminate for convenience on
[CONVENIENCE TERMINATION NOTICE] written notice.
20.4 For failure to meet minimums. Brand may terminate under Section 8.5 on [SHORTFALL NOTICE]
written notice, without proving fault and without any cure period.
20.5 Termination does not release either party from an obligation accrued before the effective
date of termination, including payment for Products delivered.

21. EFFECT OF TERMINATION
21.1 On termination, Brand may decline to accept further orders. Orders Brand has already
accepted are filled and paid for on these terms unless the parties agree otherwise in writing.
21.2 All undisputed amounts owed by Distributor become due on the effective date of termination,
and Brand may set off amounts owed to Distributor against them.

22. INVENTORY REPURCHASE
22.1 Brand may, at its option, repurchase all or part of Distributor's remaining saleable
inventory of the Products by written notice given within the Sell-Off Period. Brand is not
obliged to repurchase, and Distributor is not obliged to sell inventory Brand does not elect to
repurchase.
22.2 The repurchase price is [REPURCHASE PRICE BASIS], less any amounts Distributor owes Brand.
Freight is handled as follows: [REPURCHASE FREIGHT].
22.3 The following are excluded from repurchase: Product with less than
[REPURCHASE SHELF LIFE MINIMUM]; Product that is out of code, damaged, opened, unsealed,
soiled, or not in its original saleable case; Product Distributor did not buy from Brand; Product
in packaging or artwork Brand has discontinued; Product Distributor holds in excess of a normal
ordering cycle where Brand had given notice of termination before it was ordered; and
point-of-sale material and equipment, which is returned under Section 13.4 rather than
repurchased.
22.4 Distributor shall make the inventory available for inspection and count before repurchase and
shall provide the lot codes and code dates for it.

23. SELL-OFF
23.1 Unless Brand terminates under Section 20.2, Distributor may continue to sell its remaining
inventory of the Products to accounts in the Channels within the Territory for [SELL-OFF PERIOD]
after the effective date of termination, in the ordinary course and in accordance with Sections 7,
15 and 18.
23.2 Distributor shall not during the Sell-Off Period transship the Products outside the Territory
or the Channels, and shall not sell them to another distributor or wholesaler.
23.3 At the end of the Sell-Off Period Distributor's licence under Section 18 ends absolutely and
Distributor shall cease all use of the marks.

24. TRANSITION
24.1 For [TRANSITION PERIOD], Distributor shall cooperate in good faith in the transition of the
Products to Brand or to a successor distributor, including by providing a final depletion and
inventory report, a list of the accounts that bought the Products with contact names, the status
of open authorisations and promotional commitments, and copies of retailer paperwork relating to
the Products.
24.2 Distributor shall not, during the Term or the Sell-Off Period, disparage Brand, the Products
or any successor distributor to an account, and shall not tell an account that the Products are
discontinued when they are not.
24.3 Retailer vendor set-up, item authorisations and new-item paperwork for the Products shall be
filed in Brand's name wherever the retailer permits it.

25. CONFIDENTIALITY AND RECORDS
25.1 Each party shall keep confidential the other's non-public information, including pricing,
costs, margins, account lists, forecasts, formulas, specifications and promotional plans; shall
use it only to perform this Agreement; and shall not disclose it without the other's written
consent. These obligations continue for [CONFIDENTIALITY YEARS] years after termination and, for
information that constitutes a trade secret, for as long as it remains one.
25.2 Distributor shall keep records of purchases, depletions, accounts, lots and code dates
relating to the Products for [CONFIDENTIALITY YEARS] years and shall make them available to Brand
on reasonable notice for the purpose of verifying reports, claims, deductions and minimums.

26. ASSIGNMENT AND CHANGE OF CONTROL
26.1 Distributor shall not assign this Agreement, or delegate its performance, without Brand's
prior written consent.
26.2 A change of control of Distributor, including a sale of a majority of its equity or of
substantially all of its assets, or its acquisition by or merger with a person that distributes a
product competing with the Products, is deemed an assignment. Distributor shall notify Brand
before it occurs, and Brand may terminate on written notice given within [SHORTFALL NOTICE] of
that notice.
26.3 Brand may assign this Agreement, in whole or in part, to an affiliate or to a successor in a
merger, reorganisation or sale of substantially all of its assets or of the [BRAND NAME] brand,
without Distributor's consent. Distributor has no consent right, no right of first refusal, no
right to a payment, fee or commission, and no right to terminate, on account of a change of
control of Brand or a sale of the Products or the brand.
26.4 This Agreement binds and benefits the parties and their permitted successors and assigns.

27. INDEMNITY
27.1 Brand shall defend and indemnify Distributor and its officers, employees and agents against
third-party claims, and the resulting losses, damages, costs and reasonable legal fees, arising
from a defect in a Product as supplied by Brand, from the Products' labelling as supplied by
Brand, or from an allegation that the marks or the Products infringe a third party's
intellectual property rights.
27.2 Distributor shall defend and indemnify Brand and its officers, employees and agents against
third-party claims, and the resulting losses, damages, costs and reasonable legal fees, arising
from Distributor's storage, handling, rotation, transport or resale of the Products, from any
statement or claim about the Products that Brand did not supply or approve, from Distributor's
repackaging or alteration of the Products, or from Distributor's breach of this Agreement.
27.3 The party seeking indemnity shall give prompt written notice of the claim, allow the
indemnifying party to control the defence with counsel it selects, and cooperate at the
indemnifying party's expense. The indemnifying party shall not settle in a way that imposes an
obligation or an admission on the other without its written consent.

28. INSURANCE
28.1 Each party shall maintain commercial general liability insurance of at least
[GENERAL LIABILITY LIMIT], and Brand shall maintain product liability and products-completed
operations coverage of at least [PRODUCT LIABILITY LIMIT], together with any coverage its
operations require. Each party shall name the other as an additional insured with respect to the
Products, provide certificates of insurance on request, and give at least
[INSURANCE NOTICE PERIOD] written notice before a policy is cancelled, is not renewed or is
materially reduced. Insurance does not limit either party's obligations under this Agreement.

29. GENERAL
29.1 Neither party is liable for a failure to perform caused by an event beyond its reasonable
control, provided it notifies the other promptly and works to resume performance. Payment
obligations are not excused, and a force majeure event affecting Distributor does not suspend the
minimums in Section 8 for more than the period the event actually prevents performance.
29.2 Notices must be in writing and are effective on delivery to the addresses above or to an
address a party gives by notice.
29.3 This Agreement, with its Schedules, is the entire agreement between the parties on its
subject matter and supersedes all prior discussions, term sheets and course of dealing. It may be
amended only in a writing signed by both parties. A failure to enforce a provision is not a waiver
of it, and no remedy is exclusive of another.
29.4 If any provision is held unenforceable, the remainder continues in effect. This Agreement may
be signed in counterparts, including by electronic signature, each of which is an original.
29.5 Sections 4, 7.3, 16, 18.2, 21 through 27, and this Section 29 survive termination.
29.6 This Agreement is governed by the laws of the State of [GOVERNING LAW STATE], without regard
to its conflict of laws principles, and the parties consent to venue in [VENUE]. The parties
acknowledge that the law of the place where the Territory sits may bear on how this Agreement may
be ended, and each has had the opportunity to take its own advice on that question.

IN WITNESS WHEREOF, the parties have executed this Agreement as of the Effective Date.


[COMPANY NAME]                            [DISTRIBUTOR NAME]


By: _______________________________       By: _______________________________

Name: _____________________________       Name: _____________________________

Title: ____________________________       Title: ____________________________

Date: _____________________________       Date: _____________________________


─────────────────────────────────────────────────────────────────────────────
SCHEDULE A — TERRITORY
─────────────────────────────────────────────────────────────────────────────

Name the geography. Do not attach a map alone, and do not write "the Southwest".

States or provinces included ................ __________________________________
Counties or parishes included ............... __________________________________
Metropolitan areas included ................. __________________________________
Named cities or ZIP ranges included ......... __________________________________
Geography expressly excluded ................ __________________________________
Exclusive, sole or non-exclusive ............ __________________________________
Effective date of this Schedule ............. __________________________________

Signed for Brand ............................ __________________________________

Signed for Distributor ...................... __________________________________


─────────────────────────────────────────────────────────────────────────────
SCHEDULE B — PRODUCTS AND PRICE TO DISTRIBUTOR
─────────────────────────────────────────────────────────────────────────────

List each Product with its item code, case pack, unit size, pallet configuration, stated shelf
life, price to Distributor and the effective date of that price.


─────────────────────────────────────────────────────────────────────────────
SCHEDULE C — ACCOUNT COVERAGE
─────────────────────────────────────────────────────────────────────────────

List the chains, banners and independent accounts Distributor commits to open and service, the
target number of active points of distribution by date, and the call frequency for each tier.


─────────────────────────────────────────────────────────────────────────────
SCHEDULE D — HOUSE ACCOUNTS
─────────────────────────────────────────────────────────────────────────────

Name every account Brand services directly and that is excluded from the appointment, including
affiliates, banners and distribution centres. An account that is not named here is not a house
account.


─────────────────────────────────────────────────────────────────────────────
SCHEDULE E — PROMOTIONAL SUPPORT
─────────────────────────────────────────────────────────────────────────────

Set out the free fill allowance per new account and per period, the sample allowance, the
point-of-sale material Brand supplies, any promotional or depletion allowance and how it is
calculated, the proof of performance required, and the approval process for anything beyond
these caps. Support not described here or approved in writing is not payable.
`,
  },

  // ───────────────────────────────────────────────────────────────────────────────────────
  // 2. FOOD DISTRIBUTOR AGREEMENT
  // ───────────────────────────────────────────────────────────────────────────────────────
  {
    id: "food-distributor-agreement",
    docId: "food-distributor-agreement",
    title: "FOOD DISTRIBUTOR AGREEMENT",
    version: "1.0",
    lastReviewed: "2026-08-25",

    founderGuide: {
      useThisWhen:
        "A broadline, specialty, natural-channel or foodservice distributor is ready to list your items in its catalogue and stock them in its warehouses. Use it before you pay a new-item fee, because the fee schedule and the deduction rules are the deal.",
      whyItMatters:
        "A food distributor takes title, sets its own resale price, and deducts from your invoices before the money reaches you. Founders negotiate the case price and then lose more than the difference to fees they never modelled: catalogue listing, new-item and warehouse slotting charges, promotional funding, freight allowances, spoilage credits and returns. The number that decides whether the relationship works is not your list price — it is what actually clears per case after everything that gets taken back.",
      negotiateCarefully: [
        {
          point: "One closed schedule of every fee and deduction",
          detail:
            "Get every listing fee, slotting charge, allowance, penalty and deduction onto a single exhibit with its rate, the base it applies to and when it is taken, and say that anything not on it may not be deducted. That one sentence turns an open schedule into a number you can model.",
        },
        {
          point: "New-item fees buy something specific",
          detail:
            "If you are paying to be listed, write down what the payment buys: which catalogue, which distribution centres, which effective date, and what happens to the fee if the item is never actually stocked or is discontinued before the first review.",
        },
        {
          point: "Spoilage should be actual, evidenced and capped",
          detail:
            "A flat percentage taken off every invoice regardless of what was actually destroyed is a permanent price cut wearing a quality label. Ask for claims supported by lot, quantity and evidence, submitted inside a window, with a cap you have modelled.",
        },
        {
          point: "Code-date rules cut both ways",
          detail:
            "Distributors refuse product inside a portion of its remaining shelf life and then charge you for stock that ages in their warehouse. Put the inbound minimum and the outbound minimum in the same clause, along with the rotation standard, so both sides know the rule before a pallet is rejected at the dock.",
        },
        {
          point: "Agree the discontinuation path before you launch",
          detail:
            "Slow items get delisted. Decide now who pays freight on remaining warehouse stock, at what price it comes back, and how much notice you get, so being discontinued is a known cost rather than a surprise pallet return, freight collect.",
        },
      ],
      redFlags: [
        "A right to deduct any amount at any time with no documentation and no dispute window.",
        "A flat unsaleable or spoilage percentage taken on every invoice regardless of actual returns.",
        "Catalogue, setup and new-item fees payable up front with no listing commitment or effective date in return.",
        "Unlimited buy-ahead in front of an announced price increase.",
        "A grant covering every account in the region with no carve-outs and no account list attached.",
        "No stated process for discontinued items or remaining warehouse inventory.",
      ],
      timeToComplete: "75 minutes to fill · build your net-per-case model before you sign",
    },

    placeholders: [
      { token: "[EFFECTIVE DATE]", label: "Effective date", example: "September 1, 2026" },
      { token: "[COMPANY NAME]", label: "Your legal entity name", brandKey: "legalEntityName", example: "Acme Foods, LLC" },
      { token: "[COMPANY STATE]", label: "Your state of formation", brandKey: "stateOfFormation", example: "Delaware" },
      { token: "[COMPANY ENTITY TYPE]", label: "Your entity type", example: "limited liability company" },
      { token: "[COMPANY ADDRESS]", label: "Your address", brandKey: "principalAddress", example: "123 Main St, Austin, TX 78701" },
      { token: "[BRAND NAME]", label: "Your brand name", brandKey: "tradeName", example: "Acme" },
      { token: "[PRODUCT CATEGORY]", label: "Product category", brandKey: "productCategory", example: "shelf-stable snack" },
      { token: "[DISTRIBUTOR NAME]", label: "Distributor's legal name", example: "Northern Specialty Foods, Inc." },
      { token: "[DISTRIBUTOR STATE]", label: "Distributor's state of formation", example: "Illinois" },
      { token: "[DISTRIBUTOR ENTITY TYPE]", label: "Distributor's entity type", example: "corporation" },
      { token: "[DISTRIBUTOR ADDRESS]", label: "Distributor's address", example: "77 Warehouse Row, Chicago, IL 60632" },
      { token: "[APPOINTMENT TYPE]", label: "Exclusive or non-exclusive appointment", example: "non-exclusive" },
      { token: "[TERRITORY SUMMARY]", label: "How the territory is defined", example: "the distribution centres and service areas named in Schedule A" },
      { token: "[CHANNELS COVERED]", label: "Channels the appointment covers", example: "grocery, natural and specialty retail" },
      { token: "[EXCLUDED CHANNELS]", label: "Channels expressly excluded", example: "club, e-commerce and foodservice" },
      { token: "[DISTRIBUTION CENTERS]", label: "Distribution centres the items are stocked in", example: "the distribution centres listed in Schedule A" },
      { token: "[CATALOGUE LISTING DEADLINE]", label: "When the items must appear in the catalogue", example: "within 30 days of a complete item data package" },
      { token: "[ITEM DATA PACKAGE]", label: "What the new-item data package contains", example: "GTIN, case dimensions, nutrition panel, allergen statement, ingredient deck, certificates and images" },
      { token: "[NEW ITEM FEES]", label: "New-item and listing fees", example: "the fees listed in Schedule C, payable on first shipment" },
      { token: "[MINIMUM ORDER]", label: "Order minimum", example: "10 cases per item" },
      { token: "[MINIMUM DROP SIZE]", label: "Minimum drop size for delivered pricing", example: "1,500 pounds or 15 cases per delivery point" },
      { token: "[BELOW MINIMUM CHARGE]", label: "Charge for a below-minimum order", example: "the charge stated in Schedule C" },
      { token: "[PICKUP PRICE BASIS]", label: "Pick-up price basis", example: "Ex Works Brand's warehouse, Distributor's carrier" },
      { token: "[DELIVERED PRICE BASIS]", label: "Delivered price basis", example: "delivered to the distribution centre, freight prepaid and included" },
      { token: "[ORDER LEAD TIME]", label: "Lead time for accepted orders", example: "10 business days" },
      { token: "[PRICE CHANGE NOTICE]", label: "Notice before a price change takes effect", example: "60 days" },
      { token: "[BUY AHEAD CAP]", label: "Cap on ordering ahead of a price increase", example: "one normal ordering cycle, based on the prior 90 days" },
      { token: "[PAYMENT TERMS]", label: "Payment terms", example: "net 30 days from invoice date" },
      { token: "[EARLY PAYMENT DISCOUNT]", label: "Early payment discount, if any", example: "2% if paid within 10 days" },
      { token: "[INBOUND SHELF LIFE MINIMUM]", label: "Minimum remaining shelf life on receipt", example: "at least 85% of stated shelf life" },
      { token: "[OUTBOUND SHELF LIFE MINIMUM]", label: "Minimum remaining shelf life shipped to customers", example: "at least 50% of stated shelf life" },
      { token: "[MEASUREMENT PERIOD]", label: "Period minimums and velocity are measured over", example: "each calendar quarter" },
      { token: "[MINIMUM PURCHASE VOLUME]", label: "Minimum purchase per measurement period", example: "800 cases per quarter" },
      { token: "[VELOCITY STANDARD]", label: "Velocity standard for keeping the listing", example: "an average of 2 cases per stocking distribution centre per week" },
      { token: "[SHORTFALL PERIODS]", label: "Consecutive missed periods that trigger the remedy", example: "two" },
      { token: "[SHORTFALL NOTICE]", label: "Notice to act on a shortfall", example: "30 days" },
      { token: "[REPORTING CADENCE]", label: "Reporting cadence", example: "monthly, by the tenth business day of the following month" },
      { token: "[SPOILS ALLOWANCE]", label: "Spoilage and unsaleable allowance", example: "1% of purchases in the measurement period" },
      { token: "[SPOILS CLAIM PERIOD]", label: "Window to submit a spoilage claim", example: "45 days from the date the product became unsaleable" },
      { token: "[DEDUCTION DOCUMENTATION PERIOD]", label: "Time to supply backup for a deduction", example: "10 business days from request" },
      { token: "[DEDUCTION DISPUTE PERIOD]", label: "Window to dispute a deduction", example: "90 days" },
      { token: "[PROMOTION APPROVAL PERIOD]", label: "Time for the brand to approve promotional funding", example: "10 business days" },
      { token: "[DISCONTINUATION NOTICE]", label: "Notice before an item is discontinued", example: "60 days" },
      { token: "[DISCONTINUED STOCK PRICE BASIS]", label: "Price basis for remaining stock on discontinuation", example: "the price Distributor paid, net of allowances already taken" },
      { token: "[DISCONTINUED STOCK FREIGHT]", label: "Who pays freight on returned discontinued stock", example: "shared equally between the parties" },
      { token: "[INITIAL TERM]", label: "Initial term", example: "one year" },
      { token: "[RENEWAL TERM]", label: "Renewal term", example: "one year" },
      { token: "[NON-RENEWAL NOTICE]", label: "Notice of non-renewal", example: "60 days" },
      { token: "[CURE PERIOD]", label: "Cure period for a material breach", example: "30 days" },
      { token: "[CONVENIENCE TERMINATION NOTICE]", label: "Notice to terminate for convenience", example: "60 days" },
      { token: "[SELL-OFF PERIOD]", label: "Post-termination sell-off period", example: "60 days" },
      { token: "[GENERAL LIABILITY LIMIT]", label: "Commercial general liability limit", example: "$1,000,000 per occurrence" },
      { token: "[PRODUCT LIABILITY LIMIT]", label: "Product liability limit", example: "$2,000,000 aggregate" },
      { token: "[INSURANCE NOTICE PERIOD]", label: "Notice before insurance lapses or changes", example: "30 days" },
      { token: "[RECORD RETENTION PERIOD]", label: "How long lot and sales records are kept", example: "shelf life plus 2 years" },
      { token: "[CONFIDENTIALITY YEARS]", label: "Years confidentiality survives", example: "5" },
      { token: "[GOVERNING LAW STATE]", label: "Governing law state", example: "Delaware" },
      { token: "[VENUE]", label: "Venue for disputes", example: "Wilmington, Delaware" },
    ],

    body: `This Distribution Agreement (this "Agreement") is entered into as of [EFFECTIVE DATE] (the
"Effective Date") by and between:

  [COMPANY NAME], a [COMPANY STATE] [COMPANY ENTITY TYPE], with a principal place of business at
  [COMPANY ADDRESS] ("Brand"); and

  [DISTRIBUTOR NAME], a [DISTRIBUTOR STATE] [DISTRIBUTOR ENTITY TYPE], with a principal place of
  business at [DISTRIBUTOR ADDRESS] ("Distributor").

Brand produces and sells [PRODUCT CATEGORY] products under the [BRAND NAME] brand. Distributor
buys products into its distribution centres and resells them to retail and foodservice customers.
The parties agree as follows.

1. APPOINTMENT AND SCOPE
1.1 Brand appoints Distributor as an [APPOINTMENT TYPE] distributor of the Products listed in
Schedule B, in the Channels, within the Territory, and Distributor accepts. Distributor buys for
its own account, takes title on delivery, and resells in its own name and at its own prices.
1.2 "Territory" means [TERRITORY SUMMARY]. "Channels" means [CHANNELS COVERED]. The appointment
does not extend to [EXCLUDED CHANNELS].
1.3 Distributor shall stock the Products at [DISTRIBUTION CENTERS] and shall not transship them
outside the Territory or the Channels without Brand's prior written consent.
1.4 The parties are independent contractors. Nothing here creates a partnership, joint venture,
franchise, employment or agency relationship, and neither may bind the other.

2. RESERVED RIGHTS AND CARVE-OUTS
2.1 Brand reserves for itself and anyone Brand appoints, and the appointment does not cover:
(a) sales through Brand's own website, subscription programme and any other direct-to-consumer
    channel;
(b) sales through any e-commerce site, online retailer or third-party marketplace;
(c) the house accounts named in Schedule D, together with their affiliates, banners and
    distribution centres;
(d) military, government and institutional accounts; and
(e) any Channel excluded under Section 1.2.
2.2 No margin, commission, fee or allowance is payable to Distributor on a sale made under
Section 2.1, including a sale delivered inside the Territory.
2.3 Schedule D may be amended by written agreement.

3. NEW-ITEM SET-UP AND CATALOGUE LISTING
3.1 Brand shall supply, for each Product, a complete item data package consisting of
[ITEM DATA PACKAGE], together with current certificates for any certification claimed on the
label.
3.2 Distributor shall list each Product in its catalogue and make it orderable at
[DISTRIBUTION CENTERS] [CATALOGUE LISTING DEADLINE]. Distributor shall confirm the listing in
writing with the item number, the effective date and the distribution centres in which the item
is stocked.
3.3 [NEW ITEM FEES] are the only listing, set-up, slotting or new-item charges payable for the
Products. Distributor shall refund or credit any such fee paid for a Product that Distributor
does not list and stock by the date in Section 3.2, or that Distributor discontinues before the
end of the first Measurement Period following the listing.
3.4 Distributor shall keep the catalogue entry accurate and shall update it within a reasonable
time after Brand notifies it of a change to an item's data, packaging, certification or case
configuration.

4. ORDERS, DROP SIZE AND FREIGHT
4.1 Distributor shall order in writing, in quantities of at least [MINIMUM ORDER]. Brand shall
accept or decline in writing and shall ship accepted orders within [ORDER LEAD TIME].
4.2 Pick-up pricing is [PICKUP PRICE BASIS]. Delivered pricing is [DELIVERED PRICE BASIS] and
applies to orders meeting the minimum drop size of [MINIMUM DROP SIZE]. An order below the
minimum drop size may be shipped at pick-up pricing or carry [BELOW MINIMUM CHARGE], at Brand's
election, stated on the order acknowledgement before shipment.
4.3 Brand shall palletise, mark and label to the receiving standards of the receiving
distribution centre, including item number, case count, lot code and production or pack date on
each pallet, and shall schedule delivery appointments as that centre requires.
4.4 Distributor shall note any shortage, damage, code-date issue or discrepancy on the delivery
documents and notify Brand in writing promptly. Concealed damage shall be notified promptly after
discovery.

5. PRICE AND PAYMENT
5.1 Prices to Distributor are stated in Schedule B. Brand may change them on
[PRICE CHANGE NOTICE] written notice.
5.2 Orders placed after a price increase notice and before the new price takes effect may not
exceed [BUY AHEAD CAP]. Volume ordered above that cap is invoiced at the new price.
5.3 Payment terms are [PAYMENT TERMS]. Any early payment discount is [EARLY PAYMENT DISCOUNT] and
applies only to invoices paid in full within the stated period, calculated on the invoice amount
net of freight and taxes.
5.4 Distributor shall not set off, deduct or withhold any amount from an invoice except as
Section 9 allows.

6. CODE DATES, ROTATION AND HANDLING
6.1 Product shall carry at least [INBOUND SHELF LIFE MINIMUM] remaining on receipt at the
distribution centre. Distributor may reject product delivered short of that minimum, provided it
notifies Brand at the dock and holds the product for Brand's instructions.
6.2 Distributor shall not ship a Product to a customer with less than
[OUTBOUND SHELF LIFE MINIMUM] remaining without Brand's prior written consent.
6.3 Distributor shall rotate the Products on a first-expired, first-out basis, shall capture the
lot code and code date at receiving, and shall store and handle the Products in accordance with
the storage conditions stated on the specification for each Product.
6.4 Distributor shall keep lot-level records tying receipts to the customers that received them,
shall retain them for [RECORD RETENTION PERIOD], and shall cooperate promptly with any trace
exercise, mock recall, withdrawal or recall, including by placing stock on hold and identifying
affected customers.

7. MINIMUM PURCHASES AND VELOCITY
7.1 "Measurement Period" means [MEASUREMENT PERIOD].
7.2 Distributor shall purchase at least [MINIMUM PURCHASE VOLUME] in each Measurement Period.
7.3 Distributor shall not discontinue a Product for velocity while it meets [VELOCITY STANDARD].
Distributor shall give Brand [DISCONTINUATION NOTICE] written notice before discontinuing a
Product, together with the velocity data relied on and the remaining warehouse inventory by
distribution centre.
7.4 If Distributor misses the minimum in Section 7.2 for [SHORTFALL PERIODS] consecutive
Measurement Periods, Brand may on [SHORTFALL NOTICE] written notice convert the appointment to
non-exclusive, reduce the Territory or the Channels, remove Products from Schedule B, or
terminate this Agreement.

8. REPORTING
8.1 Distributor shall provide Brand, [REPORTING CADENCE]:
(a) sales out by Product and by customer, with quantity and net price;
(b) on-hand inventory by Product, distribution centre, lot and code date;
(c) new customers gained and lost in the period; and
(d) deductions taken in the period, by category.
8.2 Reporting failures that continue after written notice are a material breach for the purposes
of Section 15.

9. FEES, DEDUCTIONS AND CHARGEBACKS
9.1 Schedule C lists every fee, allowance, deduction, penalty and charge that may be applied to
Brand's invoices, with the rate, the base it is applied to and when it is taken. A fee, allowance
or deduction that is not in Schedule C or approved in writing under Section 10 may not be
deducted.
9.2 Distributor shall deliver with each remittance documentation identifying the invoice deducted
against, the fee or programme relied on, the customers, quantities and dates affected, and the
calculation. Where documentation is not delivered with the remittance, Distributor shall supply
it within [DEDUCTION DOCUMENTATION PERIOD] of Brand's written request.
9.3 A deduction for which documentation is not supplied within that period is repayable on demand
and may be re-invoiced.
9.4 Brand may dispute a deduction within [DEDUCTION DISPUTE PERIOD] running from the later of the
remittance and Brand's receipt of the documentation. Distributor shall respond in writing within
[DEDUCTION DOCUMENTATION PERIOD], and the parties shall escalate an unresolved dispute to a
senior representative of each party before either commences proceedings.
9.5 Neither party may assert a deduction, post-audit claim or chargeback relating to an invoice
after [DEDUCTION DISPUTE PERIOD] has run from that invoice, other than a claim of fraud.

10. PROMOTIONS AND ALLOWANCES
10.1 Promotional funding, deal periods, ad features, catalogue features, demo support and
distributor-run programmes require Brand's prior written approval. Distributor shall request
approval in writing, identifying the Products, the customers, the period, the price point, the
funding requested and the expected volume, and Brand shall approve or decline within
[PROMOTION APPROVAL PERIOD].
10.2 Distributor shall provide proof of performance for approved activity, including the
customers, the dates, the price points and the volume moved, before the allowance is credited.
Activity Brand has not approved in advance is not payable and may not be deducted.

11. SPOILAGE, UNSALEABLES AND RETURNS
11.1 Brand shall credit Distributor for Product that becomes unsaleable in Distributor's
possession through no fault of Distributor, up to [SPOILS ALLOWANCE] in each Measurement Period.
Credits are issued against actual, evidenced unsaleable product and not as a standing percentage
of invoices.
11.2 Distributor shall submit each claim within [SPOILS CLAIM PERIOD] with the Product, quantity,
lot, code date, distribution centre, reason and evidence of condition. Brand may inspect the
product or require documented destruction before issuing credit.
11.3 Brand is not responsible for product that became unsaleable because of Distributor's
storage, handling, rotation or transport, because Distributor held it past its code date, or
because Distributor over-ordered against demand.
11.4 Distributor may not return Product without a written return authorisation from Brand stating
the Products, quantities, destination and who pays freight. Product returned without
authorisation may be refused and is not credited.

12. DISCONTINUED ITEMS AND REMAINING INVENTORY
12.1 Where a Product is discontinued by either party, Brand may elect within
[DISCONTINUATION NOTICE] of the notice to repurchase Distributor's remaining saleable inventory
of that Product at [DISCONTINUED STOCK PRICE BASIS], less any amounts Distributor owes Brand.
Freight is handled as follows: [DISCONTINUED STOCK FREIGHT].
12.2 Product that is out of code, damaged, opened, not in its original saleable case, in
packaging Brand has discontinued, or carrying less than [OUTBOUND SHELF LIFE MINIMUM] is excluded
from repurchase.
12.3 If Brand does not elect to repurchase, Distributor may sell the remaining inventory in the
ordinary course to customers in the Channels within the Territory during the Sell-Off Period, and
shall not transship it or sell it to another distributor or wholesaler.

13. MARKETING, MARKS AND CLAIMS
13.1 Brand grants Distributor a non-exclusive, non-transferable, royalty-free licence to use the
[BRAND NAME] marks solely to list, advertise and resell the Products in the Channels within the
Territory during the Term, in the form and manner Brand approves. Distributor shall not register
or apply to register the marks or anything confusingly similar, shall not use them in its own
name, signage or domain, and shall not challenge Brand's ownership. Goodwill from Distributor's
use runs to Brand.
13.2 Distributor shall not make any claim about the Products beyond the claims that appear on
their current labelling and on materials Brand has supplied or approved in writing, and shall not
alter, repackage, over-sticker or relabel the Products.
13.3 Brand shall supply current sell sheets, images, item data and product information and shall
give reasonable notice of new items, packaging changes and label revisions.

14. SUGGESTED PRICING
14.1 Distributor sets its own resale prices. Nothing in this Agreement is an agreement between
the parties about the price at which Distributor resells the Products.
14.2 Brand may publish suggested retail and wholesale prices, promotional price points and a
written pricing policy that Brand adopts and applies unilaterally, and may communicate them to
Distributor for planning purposes. Whether to adopt such a policy, and how it is written and
applied, is a question for Brand's counsel before it is issued.

15. TERM AND TERMINATION
15.1 This Agreement begins on the Effective Date and continues for [INITIAL TERM], renewing for
successive periods of [RENEWAL TERM] unless either party gives written notice of non-renewal at
least [NON-RENEWAL NOTICE] before the end of the then-current term.
15.2 Either party may terminate on written notice if the other materially breaches and fails to
cure within [CURE PERIOD] after written notice describing the breach. A cure period runs once per
breach and does not restart on a repetition of the same breach.
15.3 Either party may terminate immediately if the other becomes insolvent, makes an assignment
for the benefit of creditors, has a receiver appointed over a substantial part of its assets, or
ceases to carry on business.
15.4 Either party may terminate for convenience on [CONVENIENCE TERMINATION NOTICE] written
notice. Brand may terminate under Section 7.4 on [SHORTFALL NOTICE] written notice.
15.5 On termination, orders Brand has already accepted are filled and paid for on these terms
unless the parties agree otherwise, all undisputed amounts become due, and the parties reconcile
the deduction account under Section 9 within the periods stated there.
15.6 Distributor may sell its remaining inventory in the ordinary course for [SELL-OFF PERIOD]
after termination, subject to Sections 6, 12.3 and 13, after which its licence under Section 13.1
ends absolutely.
15.7 Distributor shall on termination provide a final sales-out and inventory report, a list of
the customers that bought the Products, the status of open promotional commitments, and
reasonable cooperation in transferring the Products to Brand or a successor distributor.

16. CONFIDENTIALITY
16.1 Each party shall keep confidential the other's non-public information, including pricing,
costs, margins, customer lists, forecasts, formulas and specifications, shall use it only to
perform this Agreement, and shall not disclose it without the other's written consent. These
obligations continue for [CONFIDENTIALITY YEARS] years after termination and, for information
that constitutes a trade secret, for as long as it remains one.

17. INDEMNITY AND INSURANCE
17.1 Brand shall defend and indemnify Distributor and its officers, employees and agents against
third-party claims, and the resulting losses, damages, costs and reasonable legal fees, arising
from a defect in a Product as supplied by Brand, from the Products' labelling as supplied by
Brand, or from an allegation that the marks or the Products infringe a third party's intellectual
property rights.
17.2 Distributor shall defend and indemnify Brand and its officers, employees and agents against
third-party claims and the resulting losses arising from Distributor's storage, handling,
rotation, transport or resale of the Products, from any statement about the Products Brand did
not supply or approve, or from Distributor's breach of this Agreement.
17.3 The party seeking indemnity shall give prompt written notice, allow the indemnifying party
to control the defence with counsel it selects, and cooperate at that party's expense. No
settlement imposing an obligation or admission on the other party may be made without its written
consent.
17.4 Each party shall maintain commercial general liability insurance of at least
[GENERAL LIABILITY LIMIT], and Brand shall maintain product liability and products-completed
operations coverage of at least [PRODUCT LIABILITY LIMIT]. Each shall name the other as an
additional insured with respect to the Products, provide certificates on request, and give at
least [INSURANCE NOTICE PERIOD] written notice before a policy is cancelled, is not renewed or is
materially reduced.

18. ASSIGNMENT AND CHANGE OF CONTROL
18.1 Distributor shall not assign this Agreement or delegate its performance without Brand's prior
written consent, and a change of control of Distributor is deemed an assignment.
18.2 Brand may assign this Agreement to an affiliate or to a successor in a merger,
reorganisation or sale of substantially all of its assets or of the [BRAND NAME] brand without
Distributor's consent. Distributor has no consent right, no right of first refusal, no right to a
payment or fee, and no right to terminate, on account of a change of control of Brand.

19. GENERAL
19.1 Neither party is liable for a failure to perform caused by an event beyond its reasonable
control, provided it notifies the other promptly and works to resume performance. Payment
obligations are not excused.
19.2 Notices must be in writing and are effective on delivery to the addresses above.
19.3 This Agreement, with its Schedules, is the entire agreement between the parties on its
subject matter and supersedes all prior discussions, vendor portals, catalogues and course of
dealing. It may be amended only in a writing signed by both parties. A failure to enforce a
provision is not a waiver of it, and if any provision is held unenforceable the remainder
continues in effect. This Agreement may be signed in counterparts, including by electronic
signature.
19.4 Sections 2, 6.4, 9, 11, 12, 13.1, 15.5 through 15.7, 16, 17 and this Section 19 survive
termination.
19.5 This Agreement is governed by the laws of the State of [GOVERNING LAW STATE], without regard
to its conflict of laws principles, and the parties consent to venue in [VENUE].

IN WITNESS WHEREOF, the parties have executed this Agreement as of the Effective Date.


[COMPANY NAME]                            [DISTRIBUTOR NAME]


By: _______________________________       By: _______________________________

Name: _____________________________       Name: _____________________________

Title: ____________________________       Title: ____________________________

Date: _____________________________       Date: _____________________________


─────────────────────────────────────────────────────────────────────────────
SCHEDULE A — TERRITORY AND DISTRIBUTION CENTRES
─────────────────────────────────────────────────────────────────────────────

States, regions or service areas covered .... __________________________________
Distribution centres stocking the Products .. __________________________________
Distribution centres expressly excluded ..... __________________________________
Customer types covered ...................... __________________________________
Exclusive or non-exclusive .................. __________________________________

Signed for Brand ............................ __________________________________

Signed for Distributor ...................... __________________________________


─────────────────────────────────────────────────────────────────────────────
SCHEDULE B — PRODUCTS AND PRICE TO DISTRIBUTOR
─────────────────────────────────────────────────────────────────────────────

List each Product with its item number, GTIN, case pack, case dimensions and weight, pallet
configuration, stated shelf life, storage conditions, pick-up price, delivered price and the
effective date of each price.


─────────────────────────────────────────────────────────────────────────────
SCHEDULE C — FEES, ALLOWANCES AND DEDUCTIONS
─────────────────────────────────────────────────────────────────────────────

List every fee, allowance, deduction, penalty and charge that may be applied to Brand's invoices,
with the rate, the base it is applied to, when it is taken and what it buys. Include new-item and
catalogue listing fees, warehouse slotting or first-order allowances, promotional and deal
funding, freight and fuel allowances, below-minimum charges, unsaleable and spoilage allowances,
pallet and handling charges, and any post-audit right. Anything not listed here may not be
deducted.


─────────────────────────────────────────────────────────────────────────────
SCHEDULE D — HOUSE ACCOUNTS
─────────────────────────────────────────────────────────────────────────────

Name every customer Brand services directly and that is excluded from the appointment, including
affiliates, banners and distribution centres. A customer that is not named here is not a house
account.
`,
  },

  // ───────────────────────────────────────────────────────────────────────────────────────
  // 3. NON-EXCLUSIVE DISTRIBUTION AGREEMENT
  // ───────────────────────────────────────────────────────────────────────────────────────
  {
    id: "non-exclusive-distribution-agreement",
    docId: "non-exclusive-distribution-agreement",
    title: "NON-EXCLUSIVE DISTRIBUTION AGREEMENT",
    version: "1.0",
    lastReviewed: "2026-08-25",

    founderGuide: {
      useThisWhen:
        "For most first distribution relationships. A distributor wants to carry your product but has not yet earned, and has not paid for, the right to be the only one who can. Use this instead of an exclusive agreement whenever you are not certain.",
      whyItMatters:
        "Starting non-exclusive costs you almost nothing and protects the thing you cannot get back. A distributor that performs can earn exclusivity later, on terms you set once you have real data. A distributor that does not perform simply stops mattering, instead of holding a region while you have no way to replace it. Founders skip this version because a distributor asked for exclusivity and they did not want to seem difficult, and then spend two years unable to sell into a state through anyone else.",
      negotiateCarefully: [
        {
          point: "Say non-exclusive more than once",
          detail:
            "Put it in the title, in the grant, in a standalone reservation of rights and in the exclusivity-later clause. Ambiguity here is read against the person who drafted it, and a distributor who believed it was exclusive will behave as if it was.",
        },
        {
          point: "Focus area, not territory",
          detail:
            "Describe where you expect them to work without granting anything. A focus area sets expectations and gives you something to measure; a granted territory, even a non-exclusive one, invites an argument about who was allowed to sell where.",
        },
        {
          point: "Keep the term short and the exit symmetrical",
          detail:
            "A short initial term with termination for convenience on notice available to both sides is the whole point of this document. If you find yourself negotiating a long term here, you are negotiating a different agreement.",
        },
        {
          point: "Write down what exclusivity would cost",
          detail:
            "Set out now the volume, account coverage and reporting a distributor would have to hit to be offered exclusivity, and say it is offered only in a separate signed writing. That turns 'can we have exclusivity' from a relationship conversation into a scoreboard.",
        },
        {
          point: "Reserve every channel you actually use",
          detail:
            "Your own site, subscription, marketplace listings, house accounts and any military or government business should be reserved in this document even though nothing is exclusive, because a distributor who sees its own sales displaced will ask for compensation whether or not it has a right to it.",
        },
      ],
      redFlags: [
        "The word 'exclusive' appearing anywhere in a draft that is meant to be non-exclusive, including in a recital or an appendix.",
        "A long initial term or automatic multi-year renewal on a first relationship.",
        "Termination for convenience available to the distributor but not to you.",
        "Any implied promise not to appoint others, such as an undertaking to 'support' or 'protect' their accounts.",
        "A right for the distributor to be compensated if you later appoint someone else in the same area.",
      ],
      timeToComplete: "30 minutes to fill · same-week to sign",
    },

    placeholders: [
      { token: "[EFFECTIVE DATE]", label: "Effective date", example: "September 1, 2026" },
      { token: "[COMPANY NAME]", label: "Your legal entity name", brandKey: "legalEntityName", example: "Acme Beverage Co., LLC" },
      { token: "[COMPANY STATE]", label: "Your state of formation", brandKey: "stateOfFormation", example: "Delaware" },
      { token: "[COMPANY ENTITY TYPE]", label: "Your entity type", example: "limited liability company" },
      { token: "[COMPANY ADDRESS]", label: "Your address", brandKey: "principalAddress", example: "123 Main St, Austin, TX 78701" },
      { token: "[BRAND NAME]", label: "Your brand name", brandKey: "tradeName", example: "Acme" },
      { token: "[PRODUCT CATEGORY]", label: "Product category", brandKey: "productCategory", example: "functional beverage" },
      { token: "[DISTRIBUTOR NAME]", label: "Distributor's legal name", example: "Riverbend Distributing, LLC" },
      { token: "[DISTRIBUTOR STATE]", label: "Distributor's state of formation", example: "Colorado" },
      { token: "[DISTRIBUTOR ENTITY TYPE]", label: "Distributor's entity type", example: "limited liability company" },
      { token: "[DISTRIBUTOR ADDRESS]", label: "Distributor's address", example: "18 Depot St, Denver, CO 80216" },
      { token: "[FOCUS AREA]", label: "Where you expect them to work (no grant)", example: "the counties named in Schedule A" },
      { token: "[CHANNELS COVERED]", label: "Channels they are expected to work", example: "independent grocery and convenience" },
      { token: "[MINIMUM ORDER]", label: "Minimum order quantity", example: "one full pallet layer per SKU" },
      { token: "[ORDER LEAD TIME]", label: "Lead time for accepted orders", example: "10 business days" },
      { token: "[PRICE CHANGE NOTICE]", label: "Notice before a price change takes effect", example: "30 days" },
      { token: "[PAYMENT TERMS]", label: "Payment terms", example: "net 30 days from invoice date" },
      { token: "[CREDIT LIMIT]", label: "Credit limit", example: "$25,000 outstanding at any time" },
      { token: "[FREIGHT TERMS]", label: "Freight and delivery terms", example: "FOB Brand's warehouse, freight prepaid and added" },
      { token: "[STORAGE CONDITIONS]", label: "Storage and transport conditions", example: "dry, between 45 and 80 degrees Fahrenheit" },
      { token: "[OUTBOUND SHELF LIFE MINIMUM]", label: "Minimum remaining shelf life shipped to accounts", example: "at least 50% of stated shelf life" },
      { token: "[REPORTING CADENCE]", label: "Reporting cadence", example: "monthly, by the tenth business day of the following month" },
      { token: "[REVIEW PERIOD]", label: "Period performance is reviewed over", example: "each calendar quarter" },
      { token: "[EXCLUSIVITY TEST VOLUME]", label: "Volume that would be required to be offered exclusivity", example: "1,200 cases in each of two consecutive quarters" },
      { token: "[EXCLUSIVITY TEST ACCOUNTS]", label: "Account coverage that would be required", example: "120 active points of distribution, reported by account" },
      { token: "[FREE FILL CAP]", label: "Cap on free fill product", example: "one case per new account, up to 20 cases per quarter" },
      { token: "[PROMOTION APPROVAL PERIOD]", label: "Time for the brand to approve promotional support", example: "10 business days" },
      { token: "[SPOILS ALLOWANCE]", label: "Spoils and unsaleable allowance", example: "1% of purchases in the review period" },
      { token: "[SPOILS CLAIM PERIOD]", label: "Window to submit a spoils claim", example: "45 days from the date the product became unsaleable" },
      { token: "[DEDUCTION DISPUTE PERIOD]", label: "Window to dispute a deduction", example: "90 days" },
      { token: "[INITIAL TERM]", label: "Initial term", example: "one year" },
      { token: "[RENEWAL TERM]", label: "Renewal term", example: "one year" },
      { token: "[NON-RENEWAL NOTICE]", label: "Notice of non-renewal", example: "30 days" },
      { token: "[CURE PERIOD]", label: "Cure period for a material breach", example: "15 days" },
      { token: "[CONVENIENCE TERMINATION NOTICE]", label: "Notice to terminate for convenience", example: "30 days" },
      { token: "[SELL-OFF PERIOD]", label: "Post-termination sell-off period", example: "30 days" },
      { token: "[GENERAL LIABILITY LIMIT]", label: "Commercial general liability limit", example: "$1,000,000 per occurrence" },
      { token: "[PRODUCT LIABILITY LIMIT]", label: "Product liability limit", example: "$2,000,000 aggregate" },
      { token: "[INSURANCE NOTICE PERIOD]", label: "Notice before insurance lapses or changes", example: "30 days" },
      { token: "[RECORD RETENTION PERIOD]", label: "How long lot and account records are kept", example: "shelf life plus 2 years" },
      { token: "[CONFIDENTIALITY YEARS]", label: "Years confidentiality survives", example: "3" },
      { token: "[GOVERNING LAW STATE]", label: "Governing law state", example: "Delaware" },
      { token: "[VENUE]", label: "Venue for disputes", example: "Wilmington, Delaware" },
    ],

    body: `This Non-Exclusive Distribution Agreement (this "Agreement") is entered into as of
[EFFECTIVE DATE] (the "Effective Date") by and between:

  [COMPANY NAME], a [COMPANY STATE] [COMPANY ENTITY TYPE], with a principal place of business at
  [COMPANY ADDRESS] ("Brand"); and

  [DISTRIBUTOR NAME], a [DISTRIBUTOR STATE] [DISTRIBUTOR ENTITY TYPE], with a principal place of
  business at [DISTRIBUTOR ADDRESS] ("Distributor").

Brand produces and sells [PRODUCT CATEGORY] products under the [BRAND NAME] brand. Distributor
wishes to buy those products for resale on a non-exclusive basis. The parties agree as follows.

1. NON-EXCLUSIVE APPOINTMENT
1.1 Brand appoints Distributor as a non-exclusive distributor of the Products listed in Schedule
B, and Distributor accepts. Distributor buys for its own account, takes title on delivery, and
resells in its own name and at its own prices.
1.2 The appointment is non-exclusive in every respect. It grants no territory, no exclusive
channel, no protected account, no minimum share of Brand's business and no right of first
refusal.
1.3 Distributor's expected focus is [FOCUS AREA], in [CHANNELS COVERED]. The focus area is a
statement of where the parties expect Distributor to concentrate its effort. It is not a grant,
it is not a restriction on Brand, and it creates no right in Distributor over any geography,
account or channel.
1.4 Distributor shall not appoint a sub-distributor or resell the Products to another distributor
or wholesaler without Brand's prior written consent, and shall not represent to any person that
it is Brand's exclusive, sole, authorised-only or preferred distributor.
1.5 The parties are independent contractors. Nothing here creates a partnership, joint venture,
franchise, employment or agency relationship, and neither may bind the other.

2. BRAND'S RESERVED RIGHTS
2.1 Brand may, at any time and without notice to or consent from Distributor, and without owing
Distributor any compensation:
(a) appoint any number of other distributors, wholesalers, brokers or resellers, including in the
    focus area and in the same channels;
(b) sell the Products directly to any account, including accounts Distributor calls on;
(c) sell through Brand's own website, subscription programme and any other direct-to-consumer
    channel;
(d) sell through any e-commerce site, online retailer or third-party marketplace;
(e) service the house accounts named in Schedule D and any military, government or institutional
    account; and
(f) decline any order, discontinue any Product, and change prices in accordance with Section 4.
2.2 Distributor has no claim to a margin, commission, override, fee or compensation of any kind on
a sale made under Section 2.1, including a sale delivered to an address in the focus area or to an
account Distributor has called on.
2.3 Nothing said, written or done by Brand — including support for a particular account, a
reference to a region as Distributor's, or a period during which Brand appoints no one else —
grants exclusivity or limits Section 2.1. Exclusivity may be granted only under Section 8.

3. ORDERS
3.1 Distributor shall order in writing, in quantities of at least [MINIMUM ORDER]. Brand shall
accept or decline in writing and shall ship accepted orders within [ORDER LEAD TIME]. An order
binds Brand only when Brand accepts it.
3.2 Freight terms are [FREIGHT TERMS]. Title and risk of loss pass in accordance with those
terms.

4. PRICE AND PAYMENT
4.1 Prices to Distributor are stated in Schedule B and may be changed by Brand on
[PRICE CHANGE NOTICE] written notice. Orders accepted before a change takes effect are filled at
the prior price.
4.2 Payment terms are [PAYMENT TERMS]. Brand may set and adjust a credit limit, initially
[CREDIT LIMIT], and may hold or decline shipments while Distributor is over the limit or has an
undisputed invoice past due.
4.3 Distributor shall not set off, deduct or withhold any amount from an invoice. Where
Distributor believes a credit is due it shall submit a written claim with supporting
documentation, and Brand shall issue any credit it accepts against a future invoice. Either party
may dispute an amount within [DEDUCTION DISPUTE PERIOD] of the invoice or remittance it relates
to.

5. HANDLING, CODE DATES AND RECORDS
5.1 Distributor shall store and transport the Products within [STORAGE CONDITIONS], shall rotate
stock on a first-expired, first-out basis, and shall not ship to an account any Product with less
than [OUTBOUND SHELF LIFE MINIMUM] remaining.
5.2 Distributor shall not repackage, relabel, over-sticker, decant or otherwise alter the
Products or their packaging.
5.3 Distributor shall keep lot and code date records identifying which lots went to which
accounts for [RECORD RETENTION PERIOD], and shall cooperate promptly with any trace exercise,
mock recall, withdrawal or recall Brand conducts.

6. REPORTING
6.1 Distributor shall provide Brand, [REPORTING CADENCE], depletions by Product and account,
on-hand inventory by Product and code date, and a list of accounts opened and lost in the period.
6.2 Reports are the basis on which exclusivity could later be offered under Section 8, and
Distributor's failure to report is a material breach for the purposes of Section 9.

7. SUPPORT, SPOILS AND PROMOTIONS
7.1 Brand shall supply free fill product for new accounts up to [FREE FILL CAP], and current sell
sheets, images and point-of-sale material at Brand's cost.
7.2 Any promotional allowance, ad feature, display support, event funding or free product beyond
Section 7.1 requires Brand's prior written approval. Distributor shall request it in writing
identifying the account, quantity, period and price point, and Brand shall approve or decline
within [PROMOTION APPROVAL PERIOD]. Support Brand has not approved in advance is not payable.
7.3 Brand shall credit Product that becomes unsaleable in Distributor's possession through no
fault of Distributor, up to [SPOILS ALLOWANCE], on a claim submitted within
[SPOILS CLAIM PERIOD] with the Product, quantity, lot, code date, reason and evidence of
condition. Brand may inspect or require documented destruction before issuing credit.

8. HOW EXCLUSIVITY COULD BE EARNED
8.1 Brand is not obliged to grant exclusivity to Distributor at any time. If Distributor achieves
[EXCLUSIVITY TEST VOLUME] and [EXCLUSIVITY TEST ACCOUNTS] measured over [REVIEW PERIOD], and has
met its reporting obligations, Brand will discuss an exclusive appointment in good faith.
8.2 Any exclusivity is granted only in a separate written agreement signed by both parties that
states the territory, the channels, the minimum purchase and depletion volumes required to keep
it, the consequence of missing them, and the term. Until such an agreement is signed, this
Agreement is non-exclusive and Section 2 applies in full.

9. TERM AND TERMINATION
9.1 This Agreement begins on the Effective Date and continues for [INITIAL TERM], renewing for
successive periods of [RENEWAL TERM] unless either party gives written notice of non-renewal at
least [NON-RENEWAL NOTICE] before the end of the then-current term.
9.2 Either party may terminate for convenience on [CONVENIENCE TERMINATION NOTICE] written
notice, for any reason or none. Neither party owes the other compensation, damages, goodwill,
lost profit or reimbursement of investment on account of a termination for convenience or a
non-renewal.
9.3 Either party may terminate on written notice if the other materially breaches and fails to
cure within [CURE PERIOD] after written notice describing the breach, and immediately if the
other becomes insolvent, makes an assignment for the benefit of creditors or ceases to carry on
business.
9.4 On termination, orders Brand has already accepted are filled and paid for on these terms, all
undisputed amounts become due, and Distributor may sell its remaining inventory in the ordinary
course for [SELL-OFF PERIOD], after which its licence under Section 10 ends absolutely.
Distributor shall provide a final depletion and inventory report and a list of the accounts that
bought the Products.
9.5 Brand may, at its option and not as an obligation, repurchase saleable inventory at the price
Distributor paid, less amounts owed to Brand, excluding product that is out of code, damaged,
opened, altered, or not in its original saleable case.

10. TRADEMARKS AND CLAIMS
10.1 Brand grants Distributor a non-exclusive, non-transferable, royalty-free licence to use the
[BRAND NAME] marks solely to advertise and resell the Products during the Term, in the form and
manner Brand approves. Distributor shall not register or apply to register the marks or anything
confusingly similar, shall not use them in its own name, signage, social media handle or domain,
and shall not challenge Brand's ownership. Goodwill from Distributor's use runs to Brand.
10.2 Distributor shall not make any claim about the Products beyond the claims on their current
labelling and on materials Brand has supplied or approved in writing.

11. PRICING
11.1 Distributor sets its own resale prices. Nothing in this Agreement is an agreement between
the parties about the price at which Distributor resells the Products. Brand may publish
suggested prices and a pricing policy it adopts and applies unilaterally; whether to do so, and
how it is written, is a question for Brand's counsel.

12. CONFIDENTIALITY
12.1 Each party shall keep confidential the other's non-public information, including pricing,
costs, account lists, forecasts and specifications, shall use it only to perform this Agreement,
and shall not disclose it without the other's written consent. These obligations continue for
[CONFIDENTIALITY YEARS] years after termination and, for information that constitutes a trade
secret, for as long as it remains one.

13. INDEMNITY AND INSURANCE
13.1 Brand shall defend and indemnify Distributor against third-party claims, and the resulting
losses, damages, costs and reasonable legal fees, arising from a defect in a Product as supplied
by Brand, from its labelling as supplied by Brand, or from an allegation that the marks or the
Products infringe a third party's intellectual property rights.
13.2 Distributor shall defend and indemnify Brand against third-party claims and resulting losses
arising from Distributor's storage, handling, rotation, transport or resale of the Products, from
any statement about the Products Brand did not supply or approve, or from Distributor's breach of
this Agreement.
13.3 Each party shall maintain commercial general liability insurance of at least
[GENERAL LIABILITY LIMIT], and Brand shall maintain product liability coverage of at least
[PRODUCT LIABILITY LIMIT]. Each shall name the other as an additional insured with respect to the
Products, provide certificates on request, and give at least [INSURANCE NOTICE PERIOD] written
notice before a policy is cancelled, is not renewed or is materially reduced.

14. GENERAL
14.1 Distributor shall not assign this Agreement or delegate its performance without Brand's prior
written consent, and a change of control of Distributor is deemed an assignment. Brand may assign
to an affiliate or to a successor in a merger or a sale of substantially all of its assets or of
the [BRAND NAME] brand without Distributor's consent, and Distributor has no consent right, no
right of first refusal and no right to a payment on a change of control of Brand.
14.2 Neither party is liable for a failure to perform caused by an event beyond its reasonable
control, provided it notifies the other promptly; payment obligations are not excused.
14.3 Notices must be in writing and are effective on delivery to the addresses above. This
Agreement, with its Schedules, is the entire agreement between the parties on its subject matter
and supersedes all prior discussions and course of dealing. It may be amended only in a writing
signed by both parties, and no oral statement or course of dealing may create exclusivity. A
failure to enforce a provision is not a waiver of it, and if any provision is held unenforceable
the remainder continues in effect. This Agreement may be signed in counterparts, including by
electronic signature.
14.4 Sections 2, 5.3, 9.4, 9.5, 10, 12 and 13 survive termination.
14.5 This Agreement is governed by the laws of the State of [GOVERNING LAW STATE], without regard
to its conflict of laws principles, and the parties consent to venue in [VENUE].

IN WITNESS WHEREOF, the parties have executed this Agreement as of the Effective Date.


[COMPANY NAME]                            [DISTRIBUTOR NAME]


By: _______________________________       By: _______________________________

Name: _____________________________       Name: _____________________________

Title: ____________________________       Title: ____________________________

Date: _____________________________       Date: _____________________________


─────────────────────────────────────────────────────────────────────────────
SCHEDULE A — FOCUS AREA
─────────────────────────────────────────────────────────────────────────────

Describe where Distributor is expected to concentrate. This is not a grant and confers no
exclusivity.

Counties, metropolitan areas or cities ...... __________________________________
Channels of focus ........................... __________________________________
Account types of focus ...................... __________________________________


─────────────────────────────────────────────────────────────────────────────
SCHEDULE B — PRODUCTS AND PRICE TO DISTRIBUTOR
─────────────────────────────────────────────────────────────────────────────

List each Product with its item code, case pack, unit size, pallet configuration, stated shelf
life, price to Distributor and the effective date of that price.


─────────────────────────────────────────────────────────────────────────────
SCHEDULE D — HOUSE ACCOUNTS
─────────────────────────────────────────────────────────────────────────────

Name the accounts Brand services directly. Because this Agreement grants nothing exclusive, this
Schedule is a courtesy list rather than a carve-out, and Section 2 applies whether or not an
account appears here.
`,
  },

  // ───────────────────────────────────────────────────────────────────────────────────────
  // 4. CPG BROKER AGREEMENT
  // ───────────────────────────────────────────────────────────────────────────────────────
  {
    id: "cpg-broker-agreement",
    docId: "cpg-broker-agreement",
    title: "CPG BROKER AGREEMENT",
    version: "1.0",
    lastReviewed: "2026-08-25",

    founderGuide: {
      useThisWhen:
        "You are engaging a broker to open retail or foodservice accounts you cannot reach yourself. Sign it before the broker makes its first call, because the accounts they touch in week one are the ones you will argue about later.",
      whyItMatters:
        "Two clauses decide whether this agreement is cheap or expensive: the definition of a house account, and the post-termination tail. Get house accounts wrong and you pay commission on business you brought in yourself, sometimes indefinitely. Get the tail wrong and you keep paying a broker who has stopped working, on accounts that now reorder through a portal. And the base matters as much as the rate — a case sold at list can net far less after slotting, allowances, spoilage credits and freight deductions, so commission on gross invoice overpays on every order.",
      negotiateCarefully: [
        {
          point: "House accounts by name, in a schedule",
          detail:
            "List every account you already have or are actively working before the broker starts, and say house accounts carry no commission unless you later agree otherwise in writing. Without the list, the argument is unwinnable once the invoice exists.",
        },
        {
          point: "Earned on collection, paid on a cycle",
          detail:
            "Commission earned on order placement puts the customer's credit risk on you while the broker is already paid. Tie earning to your actual collection, and pay on a stated cycle after that, so a cancelled or unpaid order costs you nothing but time.",
        },
        {
          point: "Define the base and list the exclusions",
          detail:
            "Net collected revenue after deductions, allowances, returns, freight, taxes and free fill is a very different number from gross invoice. Name the exclusions rather than leaving them to be inferred, and set a lower rate or an actual-net calculation for deep-discount promotional volume.",
        },
        {
          point: "Reversal that matches real deduction timing",
          detail:
            "Retailer chargebacks and spoilage credits arrive months after shipment. Give yourself an automatic reversal against future commission with a window long enough to catch a deduction that lands two quarters later, and say what happens if no future commission is due.",
        },
        {
          point: "A tail with a date on it",
          detail:
            "If there is a post-termination tail at all, limit it to accounts the broker actually opened and registered, at the stated rate, for a stated period, and say plainly that it ends absolutely on that date with nothing payable afterwards.",
        },
      ],
      redFlags: [
        "No house account definition and no schedule anywhere in the agreement.",
        "Commission earned on order placement regardless of whether you are ever paid.",
        "A post-termination tail with no end date, or one covering accounts the broker did not open.",
        "Commission calculated on gross invoice with no exclusions for allowances, returns or freight.",
        "Exclusive representation across a channel with no account or revenue target attached.",
        "Retailer vendor paperwork and authorisations filed in the broker's name rather than yours.",
      ],
      timeToComplete: "45 minutes to fill · write the house account schedule before anything else",
    },

    placeholders: [
      { token: "[EFFECTIVE DATE]", label: "Effective date", example: "September 1, 2026" },
      { token: "[COMPANY NAME]", label: "Your legal entity name", brandKey: "legalEntityName", example: "Acme Beverage Co., LLC" },
      { token: "[COMPANY STATE]", label: "Your state of formation", brandKey: "stateOfFormation", example: "Delaware" },
      { token: "[COMPANY ENTITY TYPE]", label: "Your entity type", example: "limited liability company" },
      { token: "[COMPANY ADDRESS]", label: "Your address", brandKey: "principalAddress", example: "123 Main St, Austin, TX 78701" },
      { token: "[BRAND NAME]", label: "Your brand name", brandKey: "tradeName", example: "Acme" },
      { token: "[PRODUCT CATEGORY]", label: "Product category", brandKey: "productCategory", example: "functional beverage" },
      { token: "[BROKER NAME]", label: "Broker's legal name", example: "Crossroads Sales Group, LLC" },
      { token: "[BROKER STATE]", label: "Broker's state of formation", example: "Georgia" },
      { token: "[BROKER ENTITY TYPE]", label: "Broker's entity type", example: "limited liability company" },
      { token: "[BROKER ADDRESS]", label: "Broker's address", example: "2200 Peachtree Way, Atlanta, GA 30309" },
      { token: "[APPOINTMENT TYPE]", label: "Exclusive or non-exclusive appointment", example: "non-exclusive" },
      { token: "[CHANNELS COVERED]", label: "Channels the broker represents you in", example: "conventional grocery and mass" },
      { token: "[COMMISSION RATE]", label: "Commission rate", example: "5% of the Commission Base" },
      { token: "[PROMOTIONAL COMMISSION RATE]", label: "Rate on deep-discount promotional volume", example: "2.5% of the Commission Base" },
      { token: "[PROMOTIONAL DISCOUNT THRESHOLD]", label: "Discount level at which the promotional rate applies", example: "any order priced more than 20% below list" },
      { token: "[COMMISSION EXCLUSIONS]", label: "Amounts excluded from the commission base", example: "freight, fuel surcharges, pallet charges, taxes, deposits, slotting, free fill and samples" },
      { token: "[COMMISSION PAYMENT CYCLE]", label: "When earned commission is paid", example: "monthly, by the fifteenth of the following month" },
      { token: "[ACCOUNT REGISTRATION PERIOD]", label: "Time to register a prospective new account", example: "5 business days after the first contact" },
      { token: "[REGISTRATION VALIDITY]", label: "How long a registration holds without an order", example: "120 days" },
      { token: "[REORDER COMMISSION PERIOD]", label: "How long reorders from an opened account carry commission", example: "24 months from the account's first shipment" },
      { token: "[REVERSAL WINDOW]", label: "Window for reversing commission on a deduction or return", example: "180 days after the invoice was collected" },
      { token: "[REIMBURSABLE EXPENSES]", label: "Expense categories that may be reimbursed", example: "trade show booth costs, retailer sample shipping and approved travel" },
      { token: "[EXPENSE PREAPPROVAL THRESHOLD]", label: "Amount above which an expense needs pre-approval", example: "$250 per item" },
      { token: "[EXPENSE SUBMISSION PERIOD]", label: "Window to submit an expense", example: "30 days after it is incurred" },
      { token: "[MEASUREMENT PERIOD]", label: "Period performance is measured over", example: "each calendar quarter" },
      { token: "[PERFORMANCE TARGET]", label: "Performance expectation", example: "12 new authorised accounts and $150,000 of collected revenue per quarter" },
      { token: "[SHORTFALL PERIODS]", label: "Consecutive missed periods that trigger the remedy", example: "two" },
      { token: "[SHORTFALL NOTICE]", label: "Notice to act on a shortfall", example: "30 days" },
      { token: "[REPORTING CADENCE]", label: "Broker reporting cadence", example: "monthly, with a call each period" },
      { token: "[STATEMENT PERIOD]", label: "How often the brand issues a commission statement", example: "with each commission payment" },
      { token: "[STATEMENT DISPUTE PERIOD]", label: "Window to dispute a commission statement", example: "60 days" },
      { token: "[AUDIT PERIOD]", label: "How far back records may be audited", example: "the prior 24 months" },
      { token: "[INITIAL TERM]", label: "Initial term", example: "one year" },
      { token: "[RENEWAL TERM]", label: "Renewal term", example: "one year" },
      { token: "[NON-RENEWAL NOTICE]", label: "Notice of non-renewal", example: "30 days" },
      { token: "[CURE PERIOD]", label: "Cure period for a material breach", example: "15 days" },
      { token: "[CONVENIENCE TERMINATION NOTICE]", label: "Notice to terminate for convenience", example: "30 days" },
      { token: "[TAIL PERIOD]", label: "Post-termination commission tail", example: "90 days after the effective date of termination" },
      { token: "[CONFIDENTIALITY YEARS]", label: "Years confidentiality survives", example: "3" },
      { token: "[GENERAL LIABILITY LIMIT]", label: "Commercial general liability limit", example: "$1,000,000 per occurrence" },
      { token: "[INSURANCE NOTICE PERIOD]", label: "Notice before insurance lapses or changes", example: "30 days" },
      { token: "[GOVERNING LAW STATE]", label: "Governing law state", example: "Delaware" },
      { token: "[VENUE]", label: "Venue for disputes", example: "Wilmington, Delaware" },
    ],

    body: `This Broker Agreement (this "Agreement") is entered into as of [EFFECTIVE DATE] (the
"Effective Date") by and between:

  [COMPANY NAME], a [COMPANY STATE] [COMPANY ENTITY TYPE], with a principal place of business at
  [COMPANY ADDRESS] ("Brand"); and

  [BROKER NAME], a [BROKER STATE] [BROKER ENTITY TYPE], with a principal place of business at
  [BROKER ADDRESS] ("Broker").

Brand produces and sells [PRODUCT CATEGORY] products under the [BRAND NAME] brand. Broker
represents brands to trade buyers for commission. The parties agree as follows.

1. APPOINTMENT AND ROLE
1.1 Brand appoints Broker as its [APPOINTMENT TYPE] sales representative for the Products listed
in Schedule B, to the accounts named in Schedule A, in [CHANNELS COVERED], and Broker accepts.
1.2 Broker solicits orders for Brand's account. Broker does not buy the Products, does not take
title to them, does not take possession of them other than samples, and does not carry the
receivable. Brand invoices the customer, Brand collects, and Brand bears the credit risk.
1.3 Broker has no authority to accept an order, agree a price, grant an allowance, commit Brand
to a promotion, sign a vendor agreement, settle a deduction, or otherwise bind Brand. Every order
Broker solicits is subject to Brand's written acceptance.
1.4 The parties are independent contractors. Nothing here creates a partnership, joint venture,
employment or agency relationship beyond the limited representation described in Section 1.2.

2. APPOINTED ACCOUNTS, HOUSE ACCOUNTS AND EXISTING ACCOUNTS
2.1 "Appointed Accounts" are the accounts named in Schedule A, together with any account added to
Schedule A by written agreement. Broker's appointment extends only to Appointed Accounts.
2.2 "House Accounts" are the accounts named in Schedule C, together with their affiliates,
banners, distribution centres and successor entities. Brand services House Accounts itself. No
commission is payable on any sale to a House Account, whenever the order arises and however it
arises, unless Brand agrees otherwise in a signed writing.
2.3 "Existing Accounts" are accounts that, as of the Effective Date, have bought the Products,
have issued a purchase order, have authorised an item, or are named in Schedule C as being
actively worked by Brand. Existing Accounts are treated as House Accounts and carry no
commission, whether or not Broker later calls on them.
2.4 Brand's own direct-to-consumer site, subscription programme, e-commerce sales, third-party
marketplace sales, military, government and institutional accounts, sales to Brand personnel and
investors, and product supplied for sampling, demonstration or events, carry no commission.
2.5 Where Broker wishes to call on an account that is not an Appointed Account, it shall register
the account in writing within [ACCOUNT REGISTRATION PERIOD] of first contact. Brand shall accept
or decline the registration in writing. An accepted registration lapses if the account has not
issued a purchase order within [REGISTRATION VALIDITY]. An account that is not registered and
accepted is not an Appointed Account and carries no commission.

3. COMMISSION
3.1 Brand shall pay Broker [COMMISSION RATE] on the Commission Base for Qualifying Sales.
3.2 A "Qualifying Sale" is a sale of a Product to an Appointed Account, on an order Brand has
accepted, that Brand has shipped and for which Brand has been paid.
3.3 The "Commission Base" is the amount Brand actually collects for the Qualifying Sale, less:
(a) every allowance, discount, rebate, promotional fund, slotting or listing fee, freight
    allowance, spoilage or unsaleable credit, chargeback and deduction taken by or credited to the
    customer, whether taken at invoice or afterwards; (b) returns and refunds; and (c)
[COMMISSION EXCLUSIONS].
3.4 Volume sold at [PROMOTIONAL DISCOUNT THRESHOLD] carries [PROMOTIONAL COMMISSION RATE] rather
than the rate in Section 3.1.
3.5 Free fill, samples, demonstration product and product supplied at no charge carry no
commission.

4. WHEN COMMISSION IS EARNED AND WHEN IT IS PAID
4.1 Commission is earned when Brand has been paid in full for the Qualifying Sale in cleared
funds. Commission is not earned on order placement, on Brand's acceptance of an order, on
shipment or on invoicing.
4.2 Where a customer pays in part, commission is earned pro rata on the amount collected.
4.3 Commission earned in a period is paid [COMMISSION PAYMENT CYCLE].
4.4 Brand shall provide a commission statement [STATEMENT PERIOD], showing by account and by
Product the invoices collected, the deductions applied, the Commission Base, the rate and the
amount. Broker may dispute a statement within [STATEMENT DISPUTE PERIOD], failing which it is
final. On reasonable notice and not more than once in any Measurement Period, Broker may have an
independent accountant examine Brand's records for [AUDIT PERIOD] solely to verify commission,
at Broker's cost unless the examination shows an understatement Brand does not dispute.

5. REORDERS
5.1 Reorders from an Appointed Account that Broker opened carry commission at the rate in Section
3, on the same earning and payment terms, for [REORDER COMMISSION PERIOD].
5.2 After that period, reorders from that account carry no commission unless the parties agree
otherwise in writing. Commission on reorders is not open-ended and does not continue by silence
or by course of dealing.
5.3 Where an Appointed Account begins buying the Products through a distributor rather than
directly from Brand, commission is payable on Brand's collected revenue from the distributor for
volume identified as shipped to that account, and only for the period in Section 5.1. Brand shall
not be required to pay commission more than once on the same case.

6. CHARGEBACKS, DEDUCTIONS AND RETURNS
6.1 Where a customer takes a deduction or chargeback against, returns product from, or fails to
pay an invoice on which commission has been paid, the commission attributable to that amount is
reversed.
6.2 Brand shall set off a reversal against commission next payable, and where no commission is
payable within [REVERSAL WINDOW] of the reversal, Broker shall repay it within that period.
6.3 A reversal may be applied within [REVERSAL WINDOW] of the collection it relates to, which the
parties agree reflects the time retailer deductions and unsaleable credits take to appear.
6.4 Brand shall identify each reversal on the commission statement, with the account, the invoice,
the deduction or return relied on and the calculation.

7. BROKER'S OBLIGATIONS
7.1 Broker shall use commercially reasonable efforts to promote and sell the Products to the
Appointed Accounts, shall present the Products in each applicable category review during the
Term, and shall pursue authorisations, resets and promotional windows for them.
7.2 Broker shall report to Brand [REPORTING CADENCE], covering accounts called on, meetings held,
authorisations pending and won, forecasts, competitive activity and issues raised by buyers.
7.3 Broker shall file all retailer vendor set-up forms, item authorisations and new-item
paperwork for the Products in Brand's name wherever the retailer permits it, and shall provide
Brand with copies. Broker shall not register a vendor number, portal account or item in Broker's
own name for the Products.
7.4 Broker shall not make any claim about the Products beyond the claims on their current
labelling and on materials Brand has supplied or approved in writing, shall not agree a price or
an allowance, and shall not use Brand's marks other than to promote the Products in the form
Brand approves.
7.5 Broker shall disclose in writing any brand it represents that competes with the Products, and
shall notify Brand before taking on a competing brand during the Term.

8. PERFORMANCE
8.1 "Measurement Period" means [MEASUREMENT PERIOD]. Broker is expected to achieve
[PERFORMANCE TARGET] in each Measurement Period.
8.2 If Broker does not meet the expectation for [SHORTFALL PERIODS] consecutive Measurement
Periods, Brand may on [SHORTFALL NOTICE] written notice reduce the Appointed Accounts, convert an
exclusive appointment to non-exclusive, or terminate this Agreement.

9. EXPENSES
9.1 Broker bears its own costs of doing business, including salaries, vehicles, office, travel and
entertainment, except as this Section provides.
9.2 Brand shall reimburse [REIMBURSABLE EXPENSES] that Brand has approved in writing in advance.
Any single expense above [EXPENSE PREAPPROVAL THRESHOLD] requires prior written approval, and an
expense incurred without approval is not reimbursable.
9.3 Broker shall submit expenses with receipts within [EXPENSE SUBMISSION PERIOD], and Brand shall
reimburse approved expenses on the next [COMMISSION PAYMENT CYCLE].
9.4 Product supplied to Broker for samples and demonstrations remains Brand's property until
delivered to a customer, shall be used only for the Products' promotion, and shall be accounted
for on request.

10. TERM AND TERMINATION
10.1 This Agreement begins on the Effective Date and continues for [INITIAL TERM], renewing for
successive periods of [RENEWAL TERM] unless either party gives written notice of non-renewal at
least [NON-RENEWAL NOTICE] before the end of the then-current term.
10.2 Either party may terminate for convenience on [CONVENIENCE TERMINATION NOTICE] written
notice.
10.3 Either party may terminate on written notice if the other materially breaches and fails to
cure within [CURE PERIOD] after written notice describing the breach, and immediately if the
other becomes insolvent, makes an assignment for the benefit of creditors or ceases to carry on
business. Brand may terminate immediately if Broker binds Brand without authority, makes an
unapproved claim about the Products, or files retailer paperwork in Broker's own name contrary to
Section 7.3.

11. POST-TERMINATION COMMISSION
11.1 Broker is entitled to commission, on the terms of Sections 3, 4 and 6, on Qualifying Sales to
Appointed Accounts that Broker opened, where the order is accepted by Brand before the effective
date of termination or during [TAIL PERIOD].
11.2 The tail ends absolutely at the end of [TAIL PERIOD]. No commission is payable on an order
accepted after that date, whatever the account, whoever introduced it, and however it arises.
11.3 No commission is payable during the tail on House Accounts, on Existing Accounts, on accounts
Broker did not open, or on anything excluded by Section 2.4.
11.4 Commission on orders within Section 11.1 is paid on the normal cycle as and when Brand
collects, subject to reversal under Section 6.
11.5 On termination Broker shall promptly return Brand's samples, price lists, sell sheets,
customer data and confidential material, provide a final account status report for each Appointed
Account, transfer or confirm the transfer of retailer paperwork into Brand's name, and cooperate
in the orderly handover of the accounts. Broker shall not tell an account that the Products are
discontinued when they are not, and shall not disparage Brand or a successor representative.

12. CONFIDENTIALITY
12.1 Broker shall keep confidential Brand's non-public information, including pricing, costs,
margins, deduction history, account lists, forecasts, formulas and specifications; shall use it
only to perform this Agreement; and shall not disclose it, or use it for another brand it
represents, without Brand's written consent. These obligations continue for
[CONFIDENTIALITY YEARS] years after termination and, for information that constitutes a trade
secret, for as long as it remains one.

13. INDEMNITY AND INSURANCE
13.1 Broker shall defend and indemnify Brand against third-party claims, and the resulting losses,
damages, costs and reasonable legal fees, arising from Broker's acts or omissions, from any
statement or claim about the Products Brand did not supply or approve, from Broker's exceeding
its authority, or from Broker's breach of this Agreement.
13.2 Brand shall defend and indemnify Broker against third-party claims and resulting losses
arising from a defect in a Product as supplied by Brand or from its labelling as supplied by
Brand.
13.3 Broker shall maintain commercial general liability insurance of at least
[GENERAL LIABILITY LIMIT] together with any coverage its operations require, shall name Brand as
an additional insured, shall provide certificates on request, and shall give at least
[INSURANCE NOTICE PERIOD] written notice before a policy is cancelled, is not renewed or is
materially reduced.

14. GENERAL
14.1 Neither party may assign this Agreement without the other's prior written consent, except
that Brand may assign to an affiliate or to a successor in a merger or a sale of substantially all
of its assets or of the [BRAND NAME] brand. Broker has no consent right and no right to a payment
on a change of control of Brand. A change of control of Broker is deemed an assignment.
14.2 Notices must be in writing and are effective on delivery to the addresses above. This
Agreement, with its Schedules, is the entire agreement between the parties on its subject matter
and supersedes all prior discussions and course of dealing. It may be amended only in a writing
signed by both parties. A failure to enforce a provision is not a waiver of it, and if any
provision is held unenforceable the remainder continues in effect. This Agreement may be signed
in counterparts, including by electronic signature.
14.3 Sections 2, 6, 11, 12, 13 and this Section 14 survive termination.
14.4 This Agreement is governed by the laws of the State of [GOVERNING LAW STATE], without regard
to its conflict of laws principles, and the parties consent to venue in [VENUE].

IN WITNESS WHEREOF, the parties have executed this Agreement as of the Effective Date.


[COMPANY NAME]                            [BROKER NAME]


By: _______________________________       By: _______________________________

Name: _____________________________       Name: _____________________________

Title: ____________________________       Title: ____________________________

Date: _____________________________       Date: _____________________________


─────────────────────────────────────────────────────────────────────────────
SCHEDULE A — APPOINTED ACCOUNTS
─────────────────────────────────────────────────────────────────────────────

Name each account Broker is appointed to. Use the legal entity, the banner and the region or
division. An account that is not named here, or added later in writing, is not an Appointed
Account.

Account (entity and banner) ................. __________________________________
Region or division .......................... __________________________________
Buyer or category ........................... __________________________________
Exclusive or non-exclusive to Broker ........ __________________________________


─────────────────────────────────────────────────────────────────────────────
SCHEDULE B — PRODUCTS AND LIST PRICING
─────────────────────────────────────────────────────────────────────────────

List each Product with its item code, case pack, list price to the trade, and any account-specific
pricing Broker is authorised to quote.


─────────────────────────────────────────────────────────────────────────────
SCHEDULE C — HOUSE ACCOUNTS AND EXISTING ACCOUNTS
─────────────────────────────────────────────────────────────────────────────

Name every account Brand services directly, every account that has already bought or authorised
the Products, and every account Brand is actively working as at the Effective Date, including
affiliates, banners and distribution centres. No commission is payable on any of them. Complete
this Schedule before the Agreement is signed.
`,
  },

  // ───────────────────────────────────────────────────────────────────────────────────────
  // 5. COMMISSION SALES AGREEMENT
  // ───────────────────────────────────────────────────────────────────────────────────────
  {
    id: "commission-sales-agreement",
    docId: "commission-sales-agreement",
    title: "COMMISSION SALES AGREEMENT",
    version: "1.0",
    lastReviewed: "2026-08-25",

    founderGuide: {
      useThisWhen:
        "Someone is going to sell for you and be paid a percentage rather than a fee — an outside rep, a partner making introductions, a regional salesperson working for themselves. Use it as a standalone document, or as the commission schedule attached to a broader arrangement.",
      whyItMatters:
        "Almost every commission dispute traces to four terms nobody wrote down: the base the percentage applies to, the moment commission is earned, whether reorders keep paying, and how long payments continue after the relationship ends. In this category the base question is the most expensive one, because gross shipped dollars and net collected dollars are far apart once slotting, allowances, spoilage credits and freight deductions land. Writing commission against the wrong number can make your best-selling account your least profitable one.",
      negotiateCarefully: [
        {
          point: "Name the base and name the exclusions",
          detail:
            "Write the base as what you actually collect, then list what comes out by name: allowances, promotional funding, slotting, spoilage credits, returns, freight, taxes, deposits, samples and free fill. Anything you leave to inference will be argued the other way.",
        },
        {
          point: "Earn on collection, not on the order",
          detail:
            "Commission earned when an order is placed shifts the customer's credit risk onto you while the rep is already paid. Earning on collection means a cancelled, returned or unpaid order simply never generates a commission.",
        },
        {
          point: "Say what happens to reorders",
          detail:
            "Silence gets read as forever. Decide whether reorders carry commission at all, and if so for how long from the account's first shipment, and write the end date into the clause rather than leaving it to the relationship.",
        },
        {
          point: "Reversal with a realistic window",
          detail:
            "Deductions in this channel arrive late. A reversal right that expires before the deduction shows up is not a reversal right. Match the window to how long chargebacks actually take, and say how you recover when no future commission is due.",
        },
        {
          point: "Draws are advances, not salary",
          detail:
            "If you pay a draw, say whether it is recoverable, against what, and what happens to an unrecovered balance when the arrangement ends. An unrecoverable draw is compensation with a percentage attached, which is a different deal and should be priced as one.",
        },
      ],
      redFlags: [
        "Commission on gross invoice with no exclusions.",
        "Commission earned on order placement rather than on collection.",
        "No reversal mechanism for returns, deductions or chargebacks.",
        "A post-termination tail with no end date.",
        "No commission statement obligation and no right to check the calculation.",
      ],
      timeToComplete: "25 minutes to fill · model one real order through the base before you set the rate",
    },

    placeholders: [
      { token: "[EFFECTIVE DATE]", label: "Effective date", example: "September 1, 2026" },
      { token: "[COMPANY NAME]", label: "Your legal entity name", brandKey: "legalEntityName", example: "Acme Beverage Co., LLC" },
      { token: "[COMPANY STATE]", label: "Your state of formation", brandKey: "stateOfFormation", example: "Delaware" },
      { token: "[COMPANY ENTITY TYPE]", label: "Your entity type", example: "limited liability company" },
      { token: "[COMPANY ADDRESS]", label: "Your address", brandKey: "principalAddress", example: "123 Main St, Austin, TX 78701" },
      { token: "[BRAND NAME]", label: "Your brand name", brandKey: "tradeName", example: "Acme" },
      { token: "[PRODUCT CATEGORY]", label: "Product category", brandKey: "productCategory", example: "functional beverage" },
      { token: "[REP NAME]", label: "Representative's legal name", example: "Dana Ellis" },
      { token: "[REP ADDRESS]", label: "Representative's address", example: "44 Oak Lane, Nashville, TN 37203" },
      { token: "[TERRITORY OR ACCOUNTS]", label: "Territory or account list the rep works", example: "the accounts named in Schedule A" },
      { token: "[COMMISSION RATE]", label: "Commission rate", example: "5% of the Commission Base" },
      { token: "[PROMOTIONAL COMMISSION RATE]", label: "Rate on deep-discount promotional volume", example: "2.5% of the Commission Base" },
      { token: "[PROMOTIONAL DISCOUNT THRESHOLD]", label: "Discount level at which the promotional rate applies", example: "any order priced more than 20% below list" },
      { token: "[COMMISSION EXCLUSIONS]", label: "Amounts excluded from the commission base", example: "freight, fuel surcharges, pallet charges, taxes, deposits, slotting, free fill and samples" },
      { token: "[COMMISSION PAYMENT CYCLE]", label: "When earned commission is paid", example: "monthly, by the fifteenth of the following month" },
      { token: "[ACCOUNT REGISTRATION PERIOD]", label: "Time to register a prospective account", example: "5 business days after the first contact" },
      { token: "[REORDER COMMISSION PERIOD]", label: "How long reorders carry commission", example: "12 months from the account's first shipment" },
      { token: "[REVERSAL WINDOW]", label: "Window for reversing commission", example: "180 days after the invoice was collected" },
      { token: "[DRAW TERMS]", label: "Draw or advance terms, if any", example: "no draw; commission only" },
      { token: "[REIMBURSABLE EXPENSES]", label: "Expense categories that may be reimbursed", example: "approved travel and retailer sample shipping" },
      { token: "[EXPENSE PREAPPROVAL THRESHOLD]", label: "Amount above which an expense needs pre-approval", example: "$150 per item" },
      { token: "[EXPENSE SUBMISSION PERIOD]", label: "Window to submit an expense", example: "30 days after it is incurred" },
      { token: "[STATEMENT PERIOD]", label: "How often a commission statement is issued", example: "with each commission payment" },
      { token: "[STATEMENT DISPUTE PERIOD]", label: "Window to dispute a commission statement", example: "60 days" },
      { token: "[INITIAL TERM]", label: "Initial term", example: "one year" },
      { token: "[CURE PERIOD]", label: "Cure period for a material breach", example: "15 days" },
      { token: "[CONVENIENCE TERMINATION NOTICE]", label: "Notice to terminate for convenience", example: "30 days" },
      { token: "[TAIL PERIOD]", label: "Post-termination commission tail", example: "60 days after the effective date of termination" },
      { token: "[CONFIDENTIALITY YEARS]", label: "Years confidentiality survives", example: "3" },
      { token: "[GOVERNING LAW STATE]", label: "Governing law state", example: "Delaware" },
      { token: "[VENUE]", label: "Venue for disputes", example: "Wilmington, Delaware" },
    ],

    body: `This Commission Sales Agreement (this "Agreement") is entered into as of [EFFECTIVE DATE] (the
"Effective Date") by and between:

  [COMPANY NAME], a [COMPANY STATE] [COMPANY ENTITY TYPE], with a principal place of business at
  [COMPANY ADDRESS] ("Brand"); and

  [REP NAME], of [REP ADDRESS] ("Representative").

Brand produces and sells [PRODUCT CATEGORY] products under the [BRAND NAME] brand.
Representative will solicit orders for those products for commission. The parties agree as
follows.

1. SCOPE
1.1 Representative shall solicit orders for the Products listed in Schedule B from
[TERRITORY OR ACCOUNTS], and shall use commercially reasonable efforts to do so.
1.2 Representative solicits for Brand's account. Representative does not buy the Products, does
not take title, and does not carry the receivable. Brand invoices, Brand collects, and Brand
bears the credit risk.
1.3 Representative has no authority to accept an order, agree a price, grant an allowance, commit
Brand to a promotion, sign a vendor agreement or settle a deduction. Every order is subject to
Brand's written acceptance, and Brand may decline any order for any reason.
1.4 Representative shall not make any claim about the Products beyond the claims on their current
labelling and on materials Brand has supplied or approved in writing, and shall use Brand's marks
only to promote the Products in the form Brand approves.

2. ACCOUNTS
2.1 Schedule A lists the accounts Representative is engaged to work. Schedule C lists the house
and existing accounts Brand services itself, which carry no commission whenever and however an
order from them arises.
2.2 Brand's direct-to-consumer site, subscription programme, e-commerce and third-party
marketplace sales, and military, government and institutional accounts, carry no commission.
2.3 To work an account not listed in Schedule A, Representative shall register it in writing
within [ACCOUNT REGISTRATION PERIOD] of first contact, and Brand shall accept or decline the
registration in writing. An unregistered account carries no commission.

3. COMMISSION
3.1 Brand shall pay Representative [COMMISSION RATE] on the Commission Base for Qualifying Sales.
3.2 A "Qualifying Sale" is a sale of a Product to an account under Section 2.1, on an order Brand
has accepted, that Brand has shipped and for which Brand has been paid.
3.3 The "Commission Base" is the amount Brand actually collects for the Qualifying Sale, less:
(a) every allowance, discount, rebate, promotional fund, slotting or listing fee, freight
    allowance, spoilage or unsaleable credit, chargeback and deduction taken by or credited to the
    customer, whether taken at invoice or afterwards; (b) returns and refunds; and (c)
[COMMISSION EXCLUSIONS].
3.4 Volume sold at [PROMOTIONAL DISCOUNT THRESHOLD] carries [PROMOTIONAL COMMISSION RATE] rather
than the rate in Section 3.1.
3.5 Free fill, samples, demonstration product and product supplied at no charge carry no
commission.
3.6 Where more than one person may claim commission on the same order, Brand shall determine the
split in advance in writing, and Brand is not obliged to pay commission more than once on the
same case.

4. EARNING AND PAYMENT
4.1 Commission is earned when Brand has been paid in full for the Qualifying Sale in cleared
funds, and not on order placement, acceptance, shipment or invoicing. Where a customer pays in
part, commission is earned pro rata on the amount collected.
4.2 Earned commission is paid [COMMISSION PAYMENT CYCLE].
4.3 Draw or advance terms are [DRAW TERMS], which state whether a draw is paid, what it is
recoverable against, and how an unrecovered balance is treated when this Agreement ends. Any draw
is an advance against commission earned, is recoverable from commission as it is earned, is not a
guaranteed payment, and does not become one by the passage of time or by course of dealing.
4.4 Brand shall provide a commission statement [STATEMENT PERIOD] showing by account and by
Product the invoices collected, the deductions applied, the Commission Base, the rate, any
reversal and the amount payable. Representative may dispute a statement within
[STATEMENT DISPUTE PERIOD], failing which it is final, and may on reasonable notice inspect the
records supporting a disputed statement.

5. REORDERS
5.1 Reorders from an account Representative opened carry commission on the same terms for
[REORDER COMMISSION PERIOD].
5.2 After that period, reorders carry no commission unless the parties agree otherwise in writing.
Commission on reorders does not continue by silence or by course of dealing.

6. REVERSAL AND CLAWBACK
6.1 Where a customer takes a deduction or chargeback against, returns product from, or fails to
pay an invoice on which commission has been paid, the commission attributable to that amount is
reversed.
6.2 Brand shall set off a reversal against commission next payable, and where no commission is
payable within [REVERSAL WINDOW] of the reversal, Representative shall repay it within that
period.
6.3 A reversal may be applied within [REVERSAL WINDOW] of the collection it relates to, which the
parties agree reflects the time retailer deductions and unsaleable credits take to appear. Each
reversal is identified on the commission statement with the account, the invoice, the deduction
or return relied on and the calculation.

7. EXPENSES
7.1 Representative bears its own costs of doing business, including vehicle, phone, office, travel
and entertainment, except as this Section provides.
7.2 Brand shall reimburse [REIMBURSABLE EXPENSES] approved in writing in advance. Any single
expense above [EXPENSE PREAPPROVAL THRESHOLD] requires prior written approval, and an unapproved
expense is not reimbursable.
7.3 Representative shall submit expenses with receipts within [EXPENSE SUBMISSION PERIOD], and
approved expenses are reimbursed on the next [COMMISSION PAYMENT CYCLE].
7.4 Samples and demonstration product remain Brand's property until delivered to a customer and
shall be accounted for on request.

8. INTENDED RELATIONSHIP
8.1 The parties intend that Representative performs under this Agreement as an independent
contractor and not as an employee of Brand. Representative controls the manner, means, hours and
methods of performing, supplies its own equipment and workspace, may represent other
non-competing brands subject to Section 9, and is responsible for its own taxes, insurance and
any personnel it engages.
8.2 Brand does not provide Representative with employee benefits, paid leave or withholding, and
Representative is not eligible to participate in any Brand employee plan.
8.3 The parties acknowledge that how a working relationship is characterised is determined by
applicable law and by authorities other than the parties, that this Section records the parties'
intent rather than a determination, and that each party is responsible for taking its own advice
on the question. If a characterisation different from Section 8.1 is applied, the parties shall
discuss in good faith what changes to this Agreement or to the working arrangement are needed.

9. CONFIDENTIALITY AND COMPETING BRANDS
9.1 Representative shall keep confidential Brand's non-public information, including pricing,
costs, margins, deduction history, account lists, forecasts, formulas and specifications; shall
use it only to perform this Agreement; and shall not disclose it or use it for another brand
without Brand's written consent. These obligations continue for [CONFIDENTIALITY YEARS] years
after termination and, for information that constitutes a trade secret, for as long as it remains
one.
9.2 Representative shall disclose in writing any brand it represents that competes with the
Products and shall notify Brand before taking on a competing brand during the Term.

10. TERM AND TERMINATION
10.1 This Agreement begins on the Effective Date and continues for [INITIAL TERM], and thereafter
until terminated.
10.2 Either party may terminate for convenience on [CONVENIENCE TERMINATION NOTICE] written
notice, and on written notice if the other materially breaches and fails to cure within
[CURE PERIOD] after written notice describing the breach. Brand may terminate immediately if
Representative binds Brand without authority or makes an unapproved claim about the Products.
10.3 Representative is entitled to commission on Qualifying Sales from orders Brand accepts before
the effective date of termination or during [TAIL PERIOD], paid as and when Brand collects and
subject to reversal under Section 6.
10.4 The tail ends absolutely at the end of [TAIL PERIOD]. No commission is payable on an order
accepted after that date, whatever the account and however the order arises.
10.5 On termination Representative shall return Brand's samples, price lists, sell sheets,
customer data and confidential material, provide a final status report for each account, and
cooperate in an orderly handover.

11. GENERAL
11.1 Representative shall not assign this Agreement or delegate its performance without Brand's
prior written consent. Brand may assign to an affiliate or to a successor in a merger or a sale of
substantially all of its assets or of the [BRAND NAME] brand.
11.2 Notices must be in writing and are effective on delivery to the addresses above. This
Agreement, with its Schedules, is the entire agreement between the parties on its subject matter
and supersedes all prior discussions and course of dealing, and may be amended only in a writing
signed by both parties. A failure to enforce a provision is not a waiver of it, and if any
provision is held unenforceable the remainder continues in effect. This Agreement may be signed
in counterparts, including by electronic signature.
11.3 Sections 2, 6, 8, 9, 10.3 through 10.5 and this Section 11 survive termination.
11.4 This Agreement is governed by the laws of the State of [GOVERNING LAW STATE], without regard
to its conflict of laws principles, and the parties consent to venue in [VENUE].

IN WITNESS WHEREOF, the parties have executed this Agreement as of the Effective Date.


[COMPANY NAME]                            [REP NAME]


By: _______________________________       By: _______________________________

Name: _____________________________       Name: _____________________________

Title: ____________________________       Title: ____________________________

Date: _____________________________       Date: _____________________________


─────────────────────────────────────────────────────────────────────────────
SCHEDULE A — ACCOUNTS OR TERRITORY
─────────────────────────────────────────────────────────────────────────────

Named accounts .............................. __________________________________
Territory, if account-based coverage does not apply ______________________________
Channels .................................... __________________________________


─────────────────────────────────────────────────────────────────────────────
SCHEDULE B — PRODUCTS AND LIST PRICING
─────────────────────────────────────────────────────────────────────────────

List each Product with its item code, case pack, list price to the trade, and any account-specific
pricing Representative is authorised to quote.


─────────────────────────────────────────────────────────────────────────────
SCHEDULE C — HOUSE AND EXISTING ACCOUNTS
─────────────────────────────────────────────────────────────────────────────

Name every account Brand services directly and every account that has already bought or
authorised the Products as at the Effective Date, including affiliates and banners. None of them
carries commission. Complete this Schedule before the Agreement is signed.
`,
  },

  // ───────────────────────────────────────────────────────────────────────────────────────
  // 6. THIRD-PARTY LOGISTICS (3PL) AGREEMENT
  // ───────────────────────────────────────────────────────────────────────────────────────
  {
    id: "three-pl-agreement",
    docId: "three-pl-agreement",
    title: "THIRD-PARTY LOGISTICS AGREEMENT",
    version: "1.0",
    lastReviewed: "2026-08-25",

    founderGuide: {
      useThisWhen:
        "Before your first pallet leaves the co-packer for someone else's building. Also the moment you outgrow storing it yourself and a provider offers to hold and ship it for you on a rate card and an email.",
      whyItMatters:
        "This is where margin disappears one small fee at a time. Founders negotiate the per-order rate, sign, and find in month three that receiving, storage, case handling, carton fees, EDI transactions and a monthly minimum together cost more than the headline rate they argued about. The number that matters is the fully loaded cost of a real order at your real basket size, computed before you sign. Second only to that: this is the building that physically holds everything your business owns, and these terms decide whether you can get it back.",
      negotiateCarefully: [
        {
          point: "One closed, dated fee schedule with a worked example",
          detail:
            "Every fee — receiving, putaway, storage, pick, pack, order, carton, EDI, accessorial, special project — on one exhibit, fixed for a stated period, with a worked calculation of a representative order at your actual basket. Anything not on the exhibit may not be billed.",
        },
        {
          point: "Rate changes come with an exit",
          detail:
            "If they can revise the card, you need written notice and a right to terminate without penalty inside a window that starts when the notice arrives. A change right with no exit means none of your unit economics are actually agreed.",
        },
        {
          point: "Accuracy needs a measurement base, shrinkage needs a dollar figure",
          detail:
            "An accuracy percentage means nothing until the contract says what it counts — units, cases, pallets or SKUs each produce a completely different tolerance for the same words. Do the same for shrinkage: convert the allowance into cases and dollars at your cost, in the exhibit, before you agree to it.",
        },
        {
          point: "Liability on value, not on weight",
          detail:
            "A cap set by weight or per pallet can leave you recovering a fraction of what the lost product cost you. Push the basis onto the value of the goods, ask whether you can declare value, and get named as an additional insured on the coverage that actually responds.",
        },
        {
          point: "Agree the exit before you need it",
          detail:
            "Notice period, removal charges, who loads the trucks, how long it takes, when the final reconciliation lands, and the export of your lot and order data in a usable format. Narrow the lien to undisputed invoiced charges and carve out stock needed for an accepted purchase order.",
        },
      ],
      redFlags: [
        "A right to change rates at any time on short notice with no matching termination right.",
        "A lien over all inventory for any amount claimed, including amounts disputed in writing.",
        "Liability capped by weight or per pallet with no option to declare value.",
        "No stated inventory accuracy standard, no measurement base, and no defined shrinkage allowance.",
        "No lot code or expiration capture for a food, beverage or supplement product.",
        "Silence on what it costs and how long it takes to remove your inventory on termination.",
      ],
      timeToComplete: "90 minutes to fill · run your last full month of real orders through the fee exhibit first",
    },

    placeholders: [
      { token: "[EFFECTIVE DATE]", label: "Effective date", example: "September 1, 2026" },
      { token: "[COMPANY NAME]", label: "Your legal entity name", brandKey: "legalEntityName", example: "Acme Beverage Co., LLC" },
      { token: "[COMPANY STATE]", label: "Your state of formation", brandKey: "stateOfFormation", example: "Delaware" },
      { token: "[COMPANY ENTITY TYPE]", label: "Your entity type", example: "limited liability company" },
      { token: "[COMPANY ADDRESS]", label: "Your address", brandKey: "principalAddress", example: "123 Main St, Austin, TX 78701" },
      { token: "[BRAND NAME]", label: "Your brand name", brandKey: "tradeName", example: "Acme" },
      { token: "[PRODUCT CATEGORY]", label: "Product category", brandKey: "productCategory", example: "functional beverage" },
      { token: "[PROVIDER NAME]", label: "3PL's legal name", example: "Crossdock Fulfilment Services, Inc." },
      { token: "[PROVIDER STATE]", label: "3PL's state of formation", example: "Nevada" },
      { token: "[PROVIDER ENTITY TYPE]", label: "3PL's entity type", example: "corporation" },
      { token: "[PROVIDER ADDRESS]", label: "3PL's address", example: "900 Logistics Pkwy, Reno, NV 89506" },
      { token: "[FACILITIES]", label: "Facilities that will hold the goods", example: "the facilities listed in Exhibit C" },
      { token: "[SERVICES]", label: "Services being bought", example: "receiving, storage, pick, pack, direct-to-consumer and retail outbound" },
      { token: "[FEE FIXED PERIOD]", label: "How long the fee schedule is fixed", example: "12 months from the Effective Date" },
      { token: "[FEE CHANGE NOTICE]", label: "Notice before a fee change takes effect", example: "60 days" },
      { token: "[FEE CHANGE EXIT WINDOW]", label: "Window to terminate after a fee change notice", example: "30 days from the notice" },
      { token: "[MINIMUM MONTHLY COMMITMENT]", label: "Minimum monthly commitment", example: "$2,500 per month in fees" },
      { token: "[MINIMUM RAMP PERIOD]", label: "Ramp period before the minimum applies", example: "the first 4 full months" },
      { token: "[STORAGE UNIT]", label: "Unit storage is charged on", example: "per pallet position occupied" },
      { token: "[STORAGE MEASUREMENT BASIS]", label: "How the storage unit is counted", example: "a daily snapshot at 11pm, a partial pallet counted as one position, no minimum position charge per SKU" },
      { token: "[STORAGE BILLING PERIOD]", label: "How storage is billed", example: "prorated daily and invoiced monthly" },
      { token: "[RECEIVING STANDARD]", label: "Receiving and putaway turnaround", example: "received, counted and available to pick within 2 business days of arrival" },
      { token: "[RECEIVING DISCREPANCY PERIOD]", label: "Time to report a receiving discrepancy", example: "1 business day after receipt" },
      { token: "[ORDER CUTOFF]", label: "Same-day ship cutoff", example: "2pm local time on business days" },
      { token: "[ORDER SLA]", label: "Outbound service level", example: "99% of orders received before cutoff shipped the same business day" },
      { token: "[SLA REMEDY]", label: "Remedy for missing the service level", example: "a credit of the order fee for each late order, and a right to terminate if missed for 3 consecutive months" },
      { token: "[INVENTORY ACCURACY STANDARD]", label: "Inventory accuracy standard", example: "99.5%" },
      { token: "[ACCURACY MEASUREMENT BASE]", label: "What the accuracy percentage is measured against", example: "units on hand against units of record, by SKU and lot, at each cycle count" },
      { token: "[CYCLE COUNT CADENCE]", label: "Cycle count cadence", example: "a rolling count covering every SKU each month" },
      { token: "[FULL COUNT CADENCE]", label: "Full physical count cadence", example: "twice a year" },
      { token: "[RECONCILIATION PERIOD]", label: "Time to reconcile a count variance", example: "10 business days from the count" },
      { token: "[SHRINKAGE ALLOWANCE]", label: "Shrinkage allowance", example: "0.25% of units received in the period" },
      { token: "[INVENTORY REPORT CADENCE]", label: "Inventory reporting cadence", example: "daily by SKU and lot, with a monthly summary" },
      { token: "[LIABILITY BASIS]", label: "Basis on which loss and damage are valued", example: "Brand's landed cost of the goods, not weight or pallet count" },
      { token: "[LIABILITY CAP]", label: "Cap on the provider's liability", example: "the greater of the value of the affected goods and the fees paid in the prior 6 months" },
      { token: "[CLAIM NOTICE PERIOD]", label: "Time to notify a loss or damage claim", example: "30 days after discovery" },
      { token: "[CLAIM RESPONSE PERIOD]", label: "Time for the provider to respond to a claim", example: "20 business days" },
      { token: "[TEMPERATURE RANGE]", label: "Temperature commitment", example: "between 45 and 78 degrees Fahrenheit" },
      { token: "[HUMIDITY RANGE]", label: "Humidity commitment", example: "below 65% relative humidity" },
      { token: "[TEMPERATURE MONITORING]", label: "How temperature is monitored and logged", example: "continuous logging at pallet height, records provided monthly" },
      { token: "[EXCURSION NOTICE]", label: "Time to notify a temperature excursion", example: "within 4 hours of detection" },
      { token: "[OUTBOUND SHELF LIFE MINIMUM]", label: "Minimum remaining shelf life on outbound", example: "at least 50% of stated shelf life, or 120 days, whichever is greater" },
      { token: "[TRACEABILITY RESPONSE TIME]", label: "Turnaround for a traceability request", example: "4 business hours" },
      { token: "[MOCK RECALL CADENCE]", label: "Mock recall cadence", example: "once a year, at Brand's request" },
      { token: "[DATA EXPORT FORMAT]", label: "Format for exported data", example: "delimited files with a documented field list" },
      { token: "[DATA EXPORT PERIOD]", label: "Time to deliver exported data", example: "10 business days of a written request" },
      { token: "[LIEN SCOPE]", label: "What any lien may secure", example: "undisputed, invoiced storage and handling charges for the goods actually stored" },
      { token: "[INVOICE DISPUTE PERIOD]", label: "Window to dispute an invoice", example: "30 days" },
      { token: "[PAYMENT TERMS]", label: "Payment terms", example: "net 15 days from invoice date" },
      { token: "[INITIAL TERM]", label: "Initial term", example: "one year" },
      { token: "[RENEWAL TERM]", label: "Renewal term", example: "one year" },
      { token: "[NON-RENEWAL NOTICE]", label: "Notice of non-renewal", example: "60 days" },
      { token: "[CURE PERIOD]", label: "Cure period for a material breach", example: "15 days" },
      { token: "[CONVENIENCE TERMINATION NOTICE]", label: "Notice to terminate for convenience", example: "60 days" },
      { token: "[REMOVAL PERIOD]", label: "Time allowed to remove inventory", example: "30 days from the effective date of termination" },
      { token: "[REMOVAL CHARGES]", label: "Charges for removing inventory", example: "the outbound handling rate in Exhibit A, with no additional exit, project or reconfiguration fee" },
      { token: "[GENERAL LIABILITY LIMIT]", label: "Commercial general liability limit", example: "$1,000,000 per occurrence" },
      { token: "[WAREHOUSE LEGAL LIABILITY LIMIT]", label: "Warehouse legal liability coverage", example: "$1,000,000 per occurrence" },
      { token: "[INSURANCE NOTICE PERIOD]", label: "Notice before insurance lapses or changes", example: "30 days" },
      { token: "[RECORD RETENTION PERIOD]", label: "How long lot and order records are kept", example: "shelf life plus 2 years" },
      { token: "[CONFIDENTIALITY YEARS]", label: "Years confidentiality survives", example: "5" },
      { token: "[GOVERNING LAW STATE]", label: "Governing law state", example: "Delaware" },
      { token: "[VENUE]", label: "Venue for disputes", example: "Wilmington, Delaware" },
    ],

    body: `This Third-Party Logistics Agreement (this "Agreement") is entered into as of [EFFECTIVE DATE]
(the "Effective Date") by and between:

  [COMPANY NAME], a [COMPANY STATE] [COMPANY ENTITY TYPE], with a principal place of business at
  [COMPANY ADDRESS] ("Brand"); and

  [PROVIDER NAME], a [PROVIDER STATE] [PROVIDER ENTITY TYPE], with a principal place of business
  at [PROVIDER ADDRESS] ("Provider").

Brand sells [PRODUCT CATEGORY] products under the [BRAND NAME] brand and requires a third party to
receive, store, handle and ship its finished goods. The parties agree as follows.

1. SERVICES AND FACILITIES
1.1 Provider shall perform [SERVICES] for Brand at [FACILITIES]. Provider shall not move the Goods
to another facility, or subcontract any part of the Services, without Brand's prior written
consent, except for outbound carriage by common carriers.
1.2 "Goods" means all inventory, packaging, point-of-sale material and other property of Brand in
Provider's possession or control.
1.3 The parties are independent contractors. Provider takes no title to the Goods at any time and
holds them solely as bailee for Brand.

2. TITLE AND CUSTODY
2.1 Title to the Goods remains with Brand at all times. The Goods are not Provider's property, are
not available to Provider's creditors, and shall be identified in Provider's records and, where
practicable, physically, as Brand's property.
2.2 Provider shall not pledge, encumber, sell, transfer or dispose of the Goods, or grant any
security interest in them, except as Section 18 permits.

3. FEES
3.1 Exhibit A sets out every fee payable for the Services, with the rate, the unit it is charged
on and when it is charged, including receiving and putaway, storage, pick, pack, per-order,
carton and packaging, kitting, labelling, returns processing, EDI setup and per-transaction
charges, accessorial charges, and hourly rates for special projects and rework.
3.2 A charge that does not appear in Exhibit A is not payable. Provider shall not bill any
receiving, handling, administrative, technology, fuel, peak-season, minimum-per-SKU or exception
charge that Exhibit A does not state.
3.3 Exhibit A includes a worked example computing the total fee for a representative order at
Brand's actual order profile. The worked example is part of Exhibit A and shall be updated
whenever a fee changes.
3.4 The fees in Exhibit A are fixed for [FEE FIXED PERIOD]. After that, Provider may change them
on [FEE CHANGE NOTICE] written notice specifying each changed line and the new rate. Brand may
terminate this Agreement without charge by written notice given within [FEE CHANGE EXIT WINDOW],
in which case the prior fees apply until the Goods have been removed under Section 20.
3.5 Brand shall pay [MINIMUM MONTHLY COMMITMENT], against which actual fees are credited. The
minimum does not apply during [MINIMUM RAMP PERIOD], and does not apply in any month in which
Provider has failed to meet the standard in Section 7.2 or has suspended Services.

4. INVOICING AND PAYMENT
4.1 Provider shall invoice monthly, itemised by fee line and referencing the underlying receipts,
orders and storage measurements. Correct invoices are payable [PAYMENT TERMS].
4.2 Brand may dispute an invoice within [INVOICE DISPUTE PERIOD] and may withhold the disputed
amount while paying the balance. Provider shall provide supporting detail for a disputed line on
request.

5. RECEIVING AND PUTAWAY
5.1 Provider shall receive, count, inspect for visible damage, record and put away inbound Goods
so that they are [RECEIVING STANDARD].
5.2 At receiving, Provider shall capture and record for each receipt the SKU, quantity, lot code,
production or pack date, expiration or best-before date, pallet configuration, carrier, and the
condition of the shipment.
5.3 Provider shall notify Brand of any shortage, overage, damage, code-date issue or discrepancy
between the count and the shipping documents within [RECEIVING DISCREPANCY PERIOD], with
photographs and the delivery paperwork, and shall hold affected Goods for Brand's instructions
rather than returning or disposing of them.

6. STORAGE
6.1 Storage is charged [STORAGE UNIT], measured as follows: [STORAGE MEASUREMENT BASIS]. Storage
is billed [STORAGE BILLING PERIOD].
6.2 Provider shall store the Goods in a clean, dry, secure, pest-controlled area, on racking or
flooring appropriate to the product, and shall not double-stack, consolidate, restack or relocate
pallets in a way that risks damage without Brand's consent.
6.3 Provider shall maintain the storage area at [TEMPERATURE RANGE] and [HUMIDITY RANGE], shall
monitor and record conditions by [TEMPERATURE MONITORING], and shall notify Brand of any
excursion outside those ranges [EXCURSION NOTICE], together with the duration, the area affected
and the SKUs and lots involved. Provider shall provide monitoring records on request.

7. OUTBOUND AND SERVICE LEVELS
7.1 Provider shall pick, pack and ship orders received before [ORDER CUTOFF] in accordance with
Brand's routing instructions, retailer routing guides, packaging standards and labelling
requirements, which Brand shall supply and keep current.
7.2 Provider shall meet [ORDER SLA]. Where Provider fails to meet it, [SLA REMEDY].
7.3 Provider shall bear any retailer chargeback, fine or deduction caused by its failure to follow
a routing guide, appointment requirement, labelling standard or packaging standard Brand has
supplied, and Brand may set off those amounts against fees on notice describing the basis.
7.4 Provider shall not ship any Product with less than [OUTBOUND SHELF LIFE MINIMUM] remaining
without Brand's prior written approval.

8. LOT CONTROL, ROTATION AND EXPIRY
8.1 Provider shall maintain lot-level traceability for all Goods, in both directions: from an
inbound receipt to every outbound order containing that lot, and from an outbound order back to
the lots it contained.
8.2 Provider shall pick on a first-expired, first-out basis by default, and shall not substitute a
different rotation without Brand's written instruction.
8.3 Provider shall report to Brand, as part of the inventory report, on-hand quantities by lot and
expiration date, and shall alert Brand when any lot reaches the threshold Brand specifies in
writing so Brand can direct its disposition.
8.4 Provider shall not destroy, donate, liquidate, remove or dispose of any Goods, including
expired or damaged Goods, without Brand's prior written instruction, and shall document any
disposal Brand directs.

9. RECALL, WITHDRAWAL AND TRACEABILITY
9.1 Provider shall respond to a traceability request from Brand within [TRACEABILITY RESPONSE TIME]
with the receipts, lots, quantities, order numbers and consignees required to trace the affected
product in both directions.
9.2 Provider shall participate in a mock recall exercise [MOCK RECALL CADENCE] and shall provide
the results to Brand.
9.3 On Brand's instruction, Provider shall immediately place affected lots on hold, physically
segregate them, stop shipping them, identify every consignee that received them, and cooperate
with any withdrawal or recall Brand conducts. Provider shall perform hold, segregation and
reporting work at the rates in Exhibit A and shall not condition it on the settlement of any
disputed charge.

10. INVENTORY ACCURACY, COUNTS AND RECONCILIATION
10.1 Provider shall maintain inventory accuracy of at least [INVENTORY ACCURACY STANDARD],
measured as [ACCURACY MEASUREMENT BASE]. The measurement base in this Section is the only basis on
which accuracy is assessed under this Agreement.
10.2 Provider shall perform cycle counts [CYCLE COUNT CADENCE] and a full physical count
[FULL COUNT CADENCE], shall report each count to Brand, and shall permit Brand or its
representative to attend any count on reasonable notice.
10.3 Provider shall provide inventory reports [INVENTORY REPORT CADENCE], by SKU, lot and
expiration date, showing on hand, allocated, on hold and damaged quantities.
10.4 The parties shall reconcile any variance between a count and Brand's records within
[RECONCILIATION PERIOD]. Provider shall investigate and report the cause of the variance in
writing, and an unexplained variance is treated as a loss under Section 11.

11. SHRINKAGE, LOSS AND DAMAGE
11.1 The shrinkage allowance is [SHRINKAGE ALLOWANCE]. Exhibit B states that allowance converted
into cases and dollars at Brand's then-current cost, and the parties shall update that conversion
whenever the allowance or Brand's cost changes.
11.2 Provider is responsible for loss, shortage, destruction, theft, contamination or damage to the
Goods above the shrinkage allowance while the Goods are in its possession or control, and for any
loss or damage of any amount that results from Provider's failure to follow this Agreement,
Brand's written instructions, or the storage conditions in Section 6.
11.3 Loss and damage are valued at [LIABILITY BASIS]. Liability is not measured by weight, by
pallet count, by carton count, or by the fee charged for handling the affected Goods.
11.4 Provider's aggregate liability for loss of or damage to the Goods is limited to
[LIABILITY CAP]. That limit does not apply to Provider's gross negligence, wilful misconduct,
fraud, theft by its personnel, or breach of Section 2 or Section 18.
11.5 Brand shall notify a claim within [CLAIM NOTICE PERIOD], the period running from Brand's
discovery of the loss or damage rather than from any earlier date, and Provider shall accept,
reject or request further information within [CLAIM RESPONSE PERIOD]. Provider shall preserve
affected Goods, packaging and records pending resolution.

12. DAMAGED AND NON-CONFORMING GOODS
12.1 Provider shall segregate Goods that are damaged, opened, leaking, crushed, short-dated or
otherwise not saleable, record them separately in the inventory report, and hold them for Brand's
instructions.
12.2 Provider shall not return damaged Goods to saleable stock, and shall not release them to any
person other than Brand or a party Brand names in writing.

13. INSURANCE
13.1 Provider shall maintain commercial general liability insurance of at least
[GENERAL LIABILITY LIMIT], warehouse legal liability or equivalent coverage of at least
[WAREHOUSE LEGAL LIABILITY LIMIT] responding to loss of and damage to goods held for others,
together with workers compensation and any coverage its operations require.
13.2 Provider shall name Brand as an additional insured, or as a loss payee where appropriate,
with respect to the Goods; shall provide certificates of insurance on request; and shall give at
least [INSURANCE NOTICE PERIOD] written notice before a policy is cancelled, is not renewed or is
materially reduced. Insurance does not limit Provider's obligations under this Agreement.
13.3 Brand may arrange its own coverage on the Goods. Doing so does not reduce Provider's
responsibility under Section 11, and neither party's insurer has any right of subrogation against
the other for a loss the other has agreed to bear.

14. ACCESS AND AUDIT
14.1 Brand and its representatives may inspect the Goods, the areas in which they are stored, the
temperature records and the inventory records during normal business hours on reasonable notice,
and may attend counts and mock recalls.
14.2 Provider shall keep records of receipts, storage, orders, lots, expiration dates, counts,
variances, disposals and temperature monitoring relating to the Goods for
[RECORD RETENTION PERIOD].

15. DATA AND SYSTEMS
15.1 All data relating to the Goods and to Brand's orders and customers — including inventory
records, lot and expiration records, order and shipment history, customer names and addresses,
and count and variance history — is Brand's property. Provider holds it for Brand and may use it
only to perform the Services.
15.2 Provider shall not sell, license, share or use that data for its own purposes, for another
customer, or for analytics or benchmarking that identifies Brand.
15.3 Provider shall provide Brand with a complete export of that data in [DATA EXPORT FORMAT]
within [DATA EXPORT PERIOD] of a written request, at any time and for any reason, including on
and after termination, and shall not condition the export on the payment of any disputed amount.

16. CONFIDENTIALITY
16.1 Each party shall keep confidential the other's non-public information, including pricing,
costs, order volumes, customer lists, forecasts and specifications; shall use it only to perform
this Agreement; and shall not disclose it without the other's written consent. These obligations
continue for [CONFIDENTIALITY YEARS] years after termination and, for information that
constitutes a trade secret, for as long as it remains one.

17. INDEMNITY
17.1 Provider shall defend and indemnify Brand and its officers, employees and agents against
third-party claims, and the resulting losses, damages, costs and reasonable legal fees, arising
from Provider's performance or failure to perform, from its handling, storage or shipment of the
Goods, from injury to persons or property at its facilities, or from its breach of this
Agreement.
17.2 Brand shall defend and indemnify Provider against third-party claims and resulting losses
arising from a defect in the Goods as supplied to Provider or from the Goods' labelling, except
to the extent caused by Provider.
17.3 The party seeking indemnity shall give prompt written notice, allow the indemnifying party to
control the defence with counsel it selects, and cooperate at that party's expense. No settlement
imposing an obligation or admission on the other party may be made without its written consent.

18. LIEN AND RELEASE OF GOODS
18.1 Any lien or right of retention Provider may assert over the Goods is limited to [LIEN SCOPE].
18.2 Provider shall not assert a lien over, refuse to release, refuse to ship, or otherwise
withhold: Goods needed to fill an order Brand has already accepted; Goods subject to a hold,
withdrawal or recall; or any Goods on account of an amount Brand has disputed in writing under
Section 4.2, an amount not yet invoiced, or an amount owing on any other agreement between the
parties.
18.3 Provider shall not sell, auction or otherwise dispose of the Goods to satisfy any claim
without giving Brand written notice and a reasonable opportunity to pay or to remove the Goods,
and shall in all events comply with the release obligations in Section 20.

19. TERM AND TERMINATION
19.1 This Agreement begins on the Effective Date and continues for [INITIAL TERM], renewing for
successive periods of [RENEWAL TERM] unless either party gives written notice of non-renewal at
least [NON-RENEWAL NOTICE] before the end of the then-current term.
19.2 Either party may terminate for convenience on [CONVENIENCE TERMINATION NOTICE] written
notice.
19.3 Either party may terminate on written notice if the other materially breaches and fails to
cure within [CURE PERIOD] after written notice describing the breach, and immediately if the
other becomes insolvent, makes an assignment for the benefit of creditors, has a receiver
appointed over a substantial part of its assets, or ceases to carry on business.
19.4 Brand may also terminate under Section 3.4 following a fee change, and under [SLA REMEDY]
where that remedy provides a termination right.
19.5 No termination fee, exit fee, unamortised setup charge or liquidated amount is payable on
termination other than fees earned for Services performed and the removal charges in Section 20.

20. REMOVAL OF THE GOODS
20.1 On termination or expiry, or at any other time on Brand's written request, Provider shall
release the Goods to Brand or to a carrier or successor provider Brand names.
20.2 Provider shall make the Goods available for removal within [REMOVAL PERIOD], shall stage,
palletise, wrap, label and load them in the condition and configuration Brand reasonably
specifies, shall cooperate with Brand's carriers and appointment schedules, and shall not delay
release.
20.3 The only charges for removal are [REMOVAL CHARGES]. Storage continues to accrue at the
Exhibit A rate until the Goods are removed.
20.4 Provider shall deliver with the Goods a final inventory report by SKU, lot and expiration
date, a reconciliation of the final count against Brand's records, and the data export under
Section 15.3. The parties shall complete the final reconciliation and settle undisputed balances
within [RECONCILIATION PERIOD] of removal.

21. GENERAL
21.1 Neither party may assign this Agreement without the other's prior written consent, except
that either may assign to a successor in a merger or a sale of substantially all of its assets.
Provider shall give Brand written notice before a change of control of Provider, and Brand may
terminate on written notice given within [FEE CHANGE EXIT WINDOW] of that notice.
21.2 Neither party is liable for a failure to perform caused by an event beyond its reasonable
control, provided it notifies the other promptly and works to resume performance. Provider's
obligations to protect, account for and release the Goods are not suspended by such an event, and
Brand may remove the Goods under Section 20 while it continues.
21.3 Notices must be in writing and are effective on delivery to the addresses above. This
Agreement, with its Exhibits, is the entire agreement between the parties on its subject matter
and supersedes all prior discussions, quotations, rate cards, online terms and course of dealing.
It may be amended only in a writing signed by both parties, and Provider's standard terms,
portal terms and tariffs do not apply.
21.4 A failure to enforce a provision is not a waiver of it, and if any provision is held
unenforceable the remainder continues in effect. This Agreement may be signed in counterparts,
including by electronic signature.
21.5 Sections 2, 9, 11, 14.2, 15, 16, 17, 18, 20 and this Section 21 survive termination.
21.6 This Agreement is governed by the laws of the State of [GOVERNING LAW STATE], without regard
to its conflict of laws principles, and the parties consent to venue in [VENUE].

IN WITNESS WHEREOF, the parties have executed this Agreement as of the Effective Date.


[COMPANY NAME]                            [PROVIDER NAME]


By: _______________________________       By: _______________________________

Name: _____________________________       Name: _____________________________

Title: ____________________________       Title: ____________________________

Date: _____________________________       Date: _____________________________


─────────────────────────────────────────────────────────────────────────────
EXHIBIT A — FEE SCHEDULE
─────────────────────────────────────────────────────────────────────────────

Every fee payable. A charge that is not written here is not payable. Date this Exhibit and
re-date it on every change.

Exhibit date ................................ __________________________________
Fees fixed until ............................ __________________________________

INBOUND
Container or trailer unload ................. __________________________________
Receiving, per pallet ....................... __________________________________
Receiving, per carton ....................... __________________________________
Putaway, per pallet ......................... __________________________________
Palletising or reconfiguration .............. __________________________________

STORAGE
Rate per unit ............................... __________________________________
Unit and how it is counted .................. __________________________________
Billing period and proration ................ __________________________________
Any per-SKU or minimum position charge ...... __________________________________

OUTBOUND
Per order ................................... __________________________________
Per pick, first unit ........................ __________________________________
Per pick, additional unit ................... __________________________________
Full-case pick .............................. __________________________________
Pallet pick or full-pallet outbound ......... __________________________________
Carton, dunnage and packaging ............... __________________________________
Insert, kitting or assembly ................. __________________________________
Label, branded packaging or gift note ....... __________________________________
Retail or LTL order preparation ............. __________________________________
Shipping and freight, and how it is billed .. __________________________________

SYSTEMS
Onboarding and setup ........................ __________________________________
Integration or EDI setup .................... __________________________________
EDI or transaction fee, per document ........ __________________________________
Monthly technology or account fee ........... __________________________________

OTHER
Returns processing, per return .............. __________________________________
Rework, relabel or repack, per hour ......... __________________________________
Special project, per hour ................... __________________________________
Every accessorial charge, listed ............ __________________________________
Minimum monthly commitment .................. __________________________________

WORKED EXAMPLE — one representative order
Order profile used .......................... __________________________________
Units and lines in the order ................ __________________________________
Order fee ................................... __________________________________
Pick fees ................................... __________________________________
Carton and packaging ........................ __________________________________
Allocated storage ........................... __________________________________
Transaction or EDI fees ..................... __________________________________
Shipping .................................... __________________________________
TOTAL FULFILMENT COST, THIS ORDER ........... __________________________________

Signed for Brand ............................ __________________________________

Signed for Provider ......................... __________________________________


─────────────────────────────────────────────────────────────────────────────
EXHIBIT B — SHRINKAGE ALLOWANCE, CONVERTED
─────────────────────────────────────────────────────────────────────────────

Do not agree a shrinkage percentage without completing this table.

Allowance, as stated in Section 11.1 ........ __________________________________
Base it is applied to ....................... __________________________________
Expected units or cases in the period ....... __________________________________
Allowance expressed in units ................ __________________________________
Allowance expressed in cases ................ __________________________________
Brand's landed cost per case ................ __________________________________
ALLOWANCE EXPRESSED IN DOLLARS .............. __________________________________
Date this conversion was calculated ......... __________________________________


─────────────────────────────────────────────────────────────────────────────
EXHIBIT C — FACILITIES, PRODUCTS AND HANDLING REQUIREMENTS
─────────────────────────────────────────────────────────────────────────────

List each facility that will hold the Goods, and for each Product the SKU, case pack, pallet
configuration, stacking limit, stated shelf life, storage temperature and humidity requirement,
rotation standard, minimum remaining shelf life on outbound, and any retailer routing guide,
labelling or packaging standard that applies.
`,
  },

  // ───────────────────────────────────────────────────────────────────────────────────────
  // 7. WAREHOUSE AGREEMENT
  // ───────────────────────────────────────────────────────────────────────────────────────
  {
    id: "warehouse-agreement",
    docId: "warehouse-agreement",
    title: "WAREHOUSE AGREEMENT",
    version: "1.0",
    lastReviewed: "2026-08-25",

    founderGuide: {
      useThisWhen:
        "You need somewhere to put pallets and nothing else — no picking, no packing, no consumer shipments. Common after a large production run, ahead of a retail launch, or when a 3PL charges more for bulk storage than a dedicated warehouse does.",
      whyItMatters:
        "Storage looks like the simplest thing you will ever buy and produces the most argument. Two costs decide whether it works: the storage rate, and the in-and-out handling charges that are invisible until you move product. Then there is the harder question — when a count comes up short, who is accountable, and how much does the shrinkage allowance let them lose before anyone owes you anything. Founders find both answers during a stock-out, which is the worst possible time to be reading the clause.",
      negotiateCarefully: [
        {
          point: "Compare handling, not just the storage rate",
          detail:
            "In and out charges frequently cost more across a year than storage does. Get the receiving, putaway, staging, loading, pallet in, pallet out and pallet exchange rates on the same schedule as the storage rate, and model a full cycle of product in and out before you compare quotes.",
        },
        {
          point: "Say how the storage unit is counted and when the clock starts",
          detail:
            "Per pallet position with no definition means a partial pallet bills as a full one. Whole-period billing means a pallet arriving on the last day can be charged twice. Ask for prorated billing and a written rule for how positions are counted.",
        },
        {
          point: "Rotation, code dates and temperature in writing",
          detail:
            "Without a rotation standard the warehouse will rotate to suit its racking, and dated product ages at the back of a bay. Specify capture of lot and expiration at receiving, first-expired-first-out release, a temperature and humidity range with monitoring, and a minimum remaining shelf life on anything shipped out.",
        },
        {
          point: "Claim windows that run from discovery",
          detail:
            "A short window running from delivery can extinguish a claim before your first count finds the problem. Run it from discovery, and pair it with a count cadence and a written reconciliation process so shortfalls surface on a schedule rather than during a stock-out.",
        },
        {
          point: "Narrow the lien, and price the exit",
          detail:
            "A lien drafted to cover disputed or unrelated amounts turns a billing disagreement into a hostage situation. Limit it to undisputed invoiced charges for the goods actually stored, and agree the removal notice, the exit handling rate and who loads the trucks before you move anything in.",
        },
      ],
      redFlags: [
        "No stated rotation standard and no lot or expiration capture at receiving.",
        "Storage conditions described only as 'suitable' or 'industry standard'.",
        "A lien covering any amount owed on any account, including disputed charges.",
        "A claim window measured in days from delivery with no discovery trigger.",
        "A right to move, consolidate or double-stack pallets without notice or restriction.",
        "Release of your inventory conditioned on payment of all charges including disputed ones.",
      ],
      timeToComplete: "45 minutes to fill · price a full in-and-out cycle before you compare quotes",
    },

    placeholders: [
      { token: "[EFFECTIVE DATE]", label: "Effective date", example: "September 1, 2026" },
      { token: "[COMPANY NAME]", label: "Your legal entity name", brandKey: "legalEntityName", example: "Acme Beverage Co., LLC" },
      { token: "[COMPANY STATE]", label: "Your state of formation", brandKey: "stateOfFormation", example: "Delaware" },
      { token: "[COMPANY ENTITY TYPE]", label: "Your entity type", example: "limited liability company" },
      { token: "[COMPANY ADDRESS]", label: "Your address", brandKey: "principalAddress", example: "123 Main St, Austin, TX 78701" },
      { token: "[BRAND NAME]", label: "Your brand name", brandKey: "tradeName", example: "Acme" },
      { token: "[PRODUCT CATEGORY]", label: "Product category", brandKey: "productCategory", example: "functional beverage" },
      { token: "[WAREHOUSE NAME]", label: "Warehouse operator's legal name", example: "Valley Storage Partners, LLC" },
      { token: "[WAREHOUSE STATE]", label: "Operator's state of formation", example: "Ohio" },
      { token: "[WAREHOUSE ENTITY TYPE]", label: "Operator's entity type", example: "limited liability company" },
      { token: "[WAREHOUSE ADDRESS]", label: "Operator's address", example: "12 Rail Yard Dr, Columbus, OH 43219" },
      { token: "[FACILITY ADDRESS]", label: "Address of the facility holding the goods", example: "12 Rail Yard Dr, Building 3, Columbus, OH 43219" },
      { token: "[SPACE COMMITMENT]", label: "Space the operator commits to hold", example: "120 pallet positions, expandable to 200 on 30 days notice" },
      { token: "[SPACE CHANGE NOTICE]", label: "Notice to increase or reduce committed space", example: "30 days" },
      { token: "[STORAGE UNIT]", label: "Unit storage is charged on", example: "per pallet position occupied" },
      { token: "[STORAGE MEASUREMENT BASIS]", label: "How the storage unit is counted", example: "a daily snapshot at 11pm, a partial pallet counted as one position" },
      { token: "[STORAGE BILLING PERIOD]", label: "How storage is billed", example: "prorated daily and invoiced monthly" },
      { token: "[HANDLING IN CHARGE]", label: "Inbound handling charge", example: "the receiving and putaway rates in Schedule 1" },
      { token: "[HANDLING OUT CHARGE]", label: "Outbound handling charge", example: "the staging and loading rates in Schedule 1" },
      { token: "[RECEIVING STANDARD]", label: "Receiving and putaway turnaround", example: "received, counted and racked within 2 business days of arrival" },
      { token: "[RELEASE LEAD TIME]", label: "Notice needed to release a shipment", example: "2 business days" },
      { token: "[RECEIVING DISCREPANCY PERIOD]", label: "Time to report a receiving discrepancy", example: "1 business day after receipt" },
      { token: "[TEMPERATURE RANGE]", label: "Temperature commitment", example: "between 45 and 78 degrees Fahrenheit" },
      { token: "[HUMIDITY RANGE]", label: "Humidity commitment", example: "below 65% relative humidity" },
      { token: "[TEMPERATURE MONITORING]", label: "How conditions are monitored and logged", example: "continuous logging at pallet height, records provided monthly" },
      { token: "[EXCURSION NOTICE]", label: "Time to notify a condition excursion", example: "within 4 hours of detection" },
      { token: "[STACKING LIMITS]", label: "Stacking and configuration limits", example: "single-stack only, no pallet on pallet" },
      { token: "[OUTBOUND SHELF LIFE MINIMUM]", label: "Minimum remaining shelf life on outbound", example: "at least 50% of stated shelf life" },
      { token: "[INVENTORY REPORT CADENCE]", label: "Inventory reporting cadence", example: "weekly by SKU and lot" },
      { token: "[CYCLE COUNT CADENCE]", label: "Cycle count cadence", example: "monthly, covering every SKU each quarter" },
      { token: "[FULL COUNT CADENCE]", label: "Full physical count cadence", example: "twice a year" },
      { token: "[RECONCILIATION PERIOD]", label: "Time to reconcile a count variance", example: "10 business days from the count" },
      { token: "[INVENTORY ACCURACY STANDARD]", label: "Inventory accuracy standard", example: "99.5%" },
      { token: "[ACCURACY MEASUREMENT BASE]", label: "What the accuracy percentage is measured against", example: "cases on hand against cases of record, by SKU and lot, at each count" },
      { token: "[SHRINKAGE ALLOWANCE]", label: "Shrinkage allowance", example: "0.25% of cases received in the period" },
      { token: "[LIABILITY BASIS]", label: "Basis on which loss and damage are valued", example: "Brand's landed cost of the goods, not weight or pallet count" },
      { token: "[LIABILITY CAP]", label: "Cap on the operator's liability", example: "the greater of the value of the affected goods and the charges paid in the prior 6 months" },
      { token: "[CLAIM NOTICE PERIOD]", label: "Time to notify a loss or damage claim", example: "30 days after discovery" },
      { token: "[CLAIM RESPONSE PERIOD]", label: "Time for the operator to respond to a claim", example: "20 business days" },
      { token: "[ACCESS NOTICE]", label: "Notice required to visit the facility", example: "1 business day" },
      { token: "[GENERAL LIABILITY LIMIT]", label: "Commercial general liability limit", example: "$1,000,000 per occurrence" },
      { token: "[WAREHOUSE LEGAL LIABILITY LIMIT]", label: "Warehouse legal liability coverage", example: "$1,000,000 per occurrence" },
      { token: "[INSURANCE NOTICE PERIOD]", label: "Notice before insurance lapses or changes", example: "30 days" },
      { token: "[LIEN SCOPE]", label: "What any lien may secure", example: "undisputed, invoiced storage and handling charges for the goods actually stored" },
      { token: "[INVOICE DISPUTE PERIOD]", label: "Window to dispute an invoice", example: "30 days" },
      { token: "[PAYMENT TERMS]", label: "Payment terms", example: "net 15 days from invoice date" },
      { token: "[RATE FIXED PERIOD]", label: "How long rates are fixed", example: "12 months from the Effective Date" },
      { token: "[RATE CHANGE NOTICE]", label: "Notice before a rate change takes effect", example: "60 days" },
      { token: "[RATE CHANGE EXIT WINDOW]", label: "Window to terminate after a rate change notice", example: "30 days from the notice" },
      { token: "[INITIAL TERM]", label: "Initial term", example: "six months" },
      { token: "[RENEWAL TERM]", label: "Renewal term", example: "month to month" },
      { token: "[NON-RENEWAL NOTICE]", label: "Notice of non-renewal", example: "30 days" },
      { token: "[CURE PERIOD]", label: "Cure period for a material breach", example: "15 days" },
      { token: "[CONVENIENCE TERMINATION NOTICE]", label: "Notice to terminate for convenience", example: "30 days" },
      { token: "[REMOVAL PERIOD]", label: "Time allowed to remove the goods", example: "30 days from the effective date of termination" },
      { token: "[REMOVAL CHARGES]", label: "Charges for removing the goods", example: "the outbound handling rate in Schedule 1, with no additional exit or project fee" },
      { token: "[RECORD RETENTION PERIOD]", label: "How long lot and storage records are kept", example: "shelf life plus 2 years" },
      { token: "[CONFIDENTIALITY YEARS]", label: "Years confidentiality survives", example: "3" },
      { token: "[GOVERNING LAW STATE]", label: "Governing law state", example: "Delaware" },
      { token: "[VENUE]", label: "Venue for disputes", example: "Wilmington, Delaware" },
    ],

    body: `This Warehouse Agreement (this "Agreement") is entered into as of [EFFECTIVE DATE] (the
"Effective Date") by and between:

  [COMPANY NAME], a [COMPANY STATE] [COMPANY ENTITY TYPE], with a principal place of business at
  [COMPANY ADDRESS] ("Brand"); and

  [WAREHOUSE NAME], a [WAREHOUSE STATE] [WAREHOUSE ENTITY TYPE], with a principal place of business
  at [WAREHOUSE ADDRESS] ("Operator").

Brand sells [PRODUCT CATEGORY] products under the [BRAND NAME] brand and requires storage for its
finished goods. Operator stores goods for others. The parties agree as follows.

1. STORAGE AND SPACE COMMITMENT
1.1 Operator shall receive, store and release Brand's goods (the "Goods") at [FACILITY ADDRESS]
(the "Facility"). Operator shall not move the Goods to another building without Brand's prior
written consent.
1.2 Operator commits to hold [SPACE COMMITMENT] available for the Goods. Either party may request
a change to the committed space on [SPACE CHANGE NOTICE] written notice, and any change takes
effect only when confirmed in writing.
1.3 This Agreement is for storage services. Operator does not pick, pack or fulfil consumer or
retail orders except as the parties agree in a separate writing.
1.4 The parties are independent contractors. Operator takes no title to the Goods and holds them
solely as bailee for Brand.

2. TITLE AND CUSTODY
2.1 Title to the Goods remains with Brand at all times. The Goods are not Operator's property, are
not available to Operator's creditors, and shall be identified in Operator's records as Brand's
property.
2.2 Operator shall not pledge, encumber, sell, transfer or dispose of the Goods, or grant any
security interest in them, except as Section 12 permits.

3. RATES AND CHARGES
3.1 Schedule 1 sets out every charge payable, with the rate, the unit it is charged on and when it
is charged. A charge that does not appear in Schedule 1 is not payable.
3.2 Storage is charged [STORAGE UNIT], measured as [STORAGE MEASUREMENT BASIS], and billed
[STORAGE BILLING PERIOD].
3.3 Inbound handling is [HANDLING IN CHARGE]. Outbound handling is [HANDLING OUT CHARGE].
3.4 The rates in Schedule 1 are fixed for [RATE FIXED PERIOD]. After that, Operator may change
them on [RATE CHANGE NOTICE] written notice specifying each changed line, and Brand may terminate
without charge by written notice given within [RATE CHANGE EXIT WINDOW], in which case the prior
rates apply until the Goods have been removed under Section 14.
3.5 Operator shall invoice monthly, itemised by line and referencing the receipts, releases and
storage measurements. Correct invoices are payable [PAYMENT TERMS]. Brand may dispute an invoice
within [INVOICE DISPUTE PERIOD] and may withhold the disputed amount while paying the balance.

4. RECEIVING
4.1 Operator shall receive, count, inspect for visible damage, record and put away inbound Goods
so that they are [RECEIVING STANDARD].
4.2 At receiving, Operator shall capture and record for each receipt the SKU, quantity, lot code,
production or pack date, expiration or best-before date, pallet configuration, carrier and
shipment condition, and shall provide Brand with a receipt confirmation.
4.3 Operator shall notify Brand of any shortage, overage, damage, code-date issue or discrepancy
against the shipping documents within [RECEIVING DISCREPANCY PERIOD], with photographs and the
delivery paperwork, and shall hold affected Goods for Brand's instructions.

5. STORAGE CONDITIONS
5.1 Operator shall store the Goods in a clean, dry, secure, pest-controlled area, on racking or
flooring appropriate to the product, and shall observe [STACKING LIMITS]. Operator shall not
double-stack, consolidate, restack or relocate the Goods contrary to those limits, and shall
notify Brand before relocating them within the Facility.
5.2 Operator shall maintain the storage area at [TEMPERATURE RANGE] and [HUMIDITY RANGE], shall
monitor and record conditions by [TEMPERATURE MONITORING], and shall notify Brand of any
excursion outside those ranges [EXCURSION NOTICE], with the duration, the area affected and the
SKUs and lots involved. Operator shall provide monitoring records on request.
5.3 Operator shall protect the Goods from freezing and from heat, including on docks and in
staging areas, and shall not stage the Goods outdoors or in an unconditioned area other than for
the time needed to load or unload.

6. ROTATION, LOT CONTROL AND CODE DATES
6.1 Operator shall release the Goods on a first-expired, first-out basis by default, and shall not
apply a different rotation without Brand's written instruction.
6.2 Operator shall maintain lot-level records showing which lots were received on which date, and
which lots were released on which release and to whom, and shall retain them for
[RECORD RETENTION PERIOD].
6.3 Operator shall not release any Product with less than [OUTBOUND SHELF LIFE MINIMUM] remaining
without Brand's prior written approval.
6.4 On Brand's instruction, Operator shall place lots on hold, physically segregate them, stop
releasing them, identify every consignee that received them, and cooperate with any trace
exercise, withdrawal or recall Brand conducts. Operator shall not condition that cooperation on
the settlement of any disputed charge.
6.5 Operator shall not destroy, donate, liquidate or dispose of any Goods, including expired or
damaged Goods, without Brand's prior written instruction, and shall document any disposal Brand
directs.

7. RELEASES AND OUTBOUND
7.1 Brand shall give Operator [RELEASE LEAD TIME] written notice of a release, identifying the
SKUs, quantities, lots if specified, carrier and appointment.
7.2 Operator shall stage, palletise, wrap, label and load the Goods in the condition and
configuration Brand reasonably specifies, shall load the carrier at the appointed time, and shall
provide a signed bill of lading and a release confirmation identifying the lots released.

8. INVENTORY RECORDS, COUNTS AND RECONCILIATION
8.1 Operator shall provide inventory reports [INVENTORY REPORT CADENCE], by SKU, lot and
expiration date, showing on hand, on hold and damaged quantities and the movements in the period.
8.2 Operator shall perform cycle counts [CYCLE COUNT CADENCE] and a full physical count
[FULL COUNT CADENCE], shall report each count to Brand, and shall permit Brand or its
representative to attend any count on reasonable notice.
8.3 Operator shall maintain inventory accuracy of at least [INVENTORY ACCURACY STANDARD], measured
as [ACCURACY MEASUREMENT BASE]. That measurement base is the only basis on which accuracy is
assessed under this Agreement.
8.4 The parties shall reconcile any variance between a count and Brand's records within
[RECONCILIATION PERIOD]. Operator shall investigate and report the cause in writing, and an
unexplained variance is treated as a loss under Section 9.

9. LOSS, DAMAGE AND SHRINKAGE
9.1 The shrinkage allowance is [SHRINKAGE ALLOWANCE]. Schedule 2 states that allowance converted
into cases and dollars at Brand's then-current cost, and the parties shall update that conversion
whenever the allowance or Brand's cost changes.
9.2 Operator is responsible for loss, shortage, destruction, theft, contamination or damage to the
Goods above the shrinkage allowance while the Goods are in its possession or control, and for any
loss or damage of any amount resulting from Operator's failure to follow this Agreement, Brand's
written instructions, or the storage conditions in Section 5.
9.3 Loss and damage are valued at [LIABILITY BASIS]. Liability is not measured by weight, by
pallet count, by carton count, or by the charge for handling the affected Goods.
9.4 Operator's aggregate liability for loss of or damage to the Goods is limited to
[LIABILITY CAP]. That limit does not apply to Operator's gross negligence, wilful misconduct,
fraud, theft by its personnel, or breach of Section 2 or Section 12.
9.5 Brand shall notify a claim within [CLAIM NOTICE PERIOD], the period running from Brand's
discovery of the loss or damage rather than from any earlier date, and Operator shall accept,
reject or request further information within [CLAIM RESPONSE PERIOD]. Operator shall preserve
affected Goods, packaging and records pending resolution.
9.6 Operator shall segregate damaged, opened, leaking, crushed or short-dated Goods, record them
separately in the inventory report, and hold them for Brand's instructions. Operator shall not
return damaged Goods to saleable stock and shall not release them to any person other than Brand
or a party Brand names in writing.

10. ACCESS AND RECORDS
10.1 Brand and its representatives may enter the Facility to inspect the Goods, the storage areas,
the monitoring records and the inventory records during normal business hours on [ACCESS NOTICE],
subject to Operator's reasonable safety and security requirements, and may attend counts.
10.2 Operator shall keep records of receipts, storage, releases, lots, expiration dates, counts,
variances, disposals and condition monitoring relating to the Goods for [RECORD RETENTION PERIOD],
and shall provide copies to Brand on request, including after termination.
10.3 Inventory, lot, receipt and release data relating to the Goods is Brand's property. Operator
holds it for Brand, may use it only to perform this Agreement, and shall not condition its
delivery on the payment of any disputed amount.

11. INSURANCE
11.1 Operator shall maintain commercial general liability insurance of at least
[GENERAL LIABILITY LIMIT] and warehouse legal liability or equivalent coverage of at least
[WAREHOUSE LEGAL LIABILITY LIMIT] responding to loss of and damage to goods held for others,
together with workers compensation and any coverage its operations require. Operator shall name
Brand as an additional insured or loss payee with respect to the Goods, provide certificates on
request, and give at least [INSURANCE NOTICE PERIOD] written notice before a policy is cancelled,
is not renewed or is materially reduced.
11.2 Operator's coverage insures Operator's operations and its liability for goods in its custody.
It is not property insurance on the Goods for Brand's benefit. Brand is responsible for insuring
the Goods against loss and damage if Brand wishes them insured for their full value, and Brand may
arrange that coverage at any time.
11.3 Brand arranging its own coverage does not reduce Operator's responsibility under Section 9,
and neither party's insurer has any right of subrogation against the other for a loss the other
has agreed to bear.

12. LIEN AND RELEASE OF THE GOODS
12.1 Any lien or right of retention Operator may assert over the Goods is limited to [LIEN SCOPE].
12.2 Operator shall not assert a lien over, refuse to release, or otherwise withhold: Goods
subject to a hold, withdrawal or recall; or any Goods on account of an amount Brand has disputed
in writing under Section 3.5, an amount not yet invoiced, or an amount owing under any other
agreement between the parties.
12.3 Operator shall not sell, auction or otherwise dispose of the Goods to satisfy any claim
without giving Brand written notice and a reasonable opportunity to pay or to remove the Goods,
and shall in all events comply with Section 14.

13. TERM AND TERMINATION
13.1 This Agreement begins on the Effective Date and continues for [INITIAL TERM], renewing for
successive periods of [RENEWAL TERM] unless either party gives written notice of non-renewal at
least [NON-RENEWAL NOTICE] before the end of the then-current term.
13.2 Either party may terminate for convenience on [CONVENIENCE TERMINATION NOTICE] written
notice.
13.3 Either party may terminate on written notice if the other materially breaches and fails to
cure within [CURE PERIOD] after written notice describing the breach, and immediately if the
other becomes insolvent, makes an assignment for the benefit of creditors, has a receiver
appointed over a substantial part of its assets, or ceases to carry on business. Brand may also
terminate under Section 3.4 following a rate change.
13.4 No termination fee, exit fee or liquidated amount is payable on termination other than
charges earned for services performed and the removal charges in Section 14.

14. REMOVAL OF THE GOODS
14.1 On termination or expiry, or at any other time on Brand's written request, Operator shall
release the Goods to Brand or to a carrier or successor provider Brand names.
14.2 Operator shall make the Goods available for removal within [REMOVAL PERIOD], shall stage,
palletise, wrap, label and load them as Brand reasonably specifies, shall cooperate with Brand's
carriers and appointment schedule, and shall not delay release.
14.3 The only charges for removal are [REMOVAL CHARGES]. Storage continues to accrue at the
Schedule 1 rate until the Goods are removed.
14.4 Operator shall deliver with the Goods a final inventory report by SKU, lot and expiration
date, a reconciliation of the final count against Brand's records, and copies of the lot and
condition records under Section 10.2. The parties shall complete the final reconciliation and
settle undisputed balances within [RECONCILIATION PERIOD] of removal.

15. CONFIDENTIALITY AND INDEMNITY
15.1 Each party shall keep confidential the other's non-public information, including pricing,
costs, volumes, customer information and specifications; shall use it only to perform this
Agreement; and shall not disclose it without the other's written consent. These obligations
continue for [CONFIDENTIALITY YEARS] years after termination.
15.2 Operator shall defend and indemnify Brand and its officers, employees and agents against
third-party claims, and the resulting losses, damages, costs and reasonable legal fees, arising
from Operator's performance or failure to perform, from its storage or handling of the Goods,
from injury to persons or property at the Facility, or from its breach of this Agreement.
15.3 Brand shall defend and indemnify Operator against third-party claims and resulting losses
arising from a defect in the Goods as supplied to Operator or from the Goods' labelling, except to
the extent caused by Operator.
15.4 The party seeking indemnity shall give prompt written notice, allow the indemnifying party to
control the defence with counsel it selects, and cooperate at that party's expense. No settlement
imposing an obligation or admission on the other party may be made without its written consent.

16. GENERAL
16.1 Neither party may assign this Agreement without the other's prior written consent, except to
a successor in a merger or a sale of substantially all of its assets. Operator shall give Brand
written notice before a change of control of Operator, and Brand may terminate on written notice
given within [RATE CHANGE EXIT WINDOW] of that notice.
16.2 Neither party is liable for a failure to perform caused by an event beyond its reasonable
control, provided it notifies the other promptly and works to resume performance. Operator's
obligations to protect, account for and release the Goods are not suspended by such an event, and
Brand may remove the Goods under Section 14 while it continues.
16.3 Notices must be in writing and are effective on delivery to the addresses above. This
Agreement, with its Schedules, is the entire agreement between the parties on its subject matter
and supersedes all prior discussions, quotations, rate sheets, online terms and course of dealing.
It may be amended only in a writing signed by both parties, and Operator's standard terms, portal
terms and tariffs do not apply.
16.4 A failure to enforce a provision is not a waiver of it, and if any provision is held
unenforceable the remainder continues in effect. This Agreement may be signed in counterparts,
including by electronic signature.
16.5 Sections 2, 6.4, 9, 10, 12, 14, 15 and this Section 16 survive termination.
16.6 This Agreement is governed by the laws of the State of [GOVERNING LAW STATE], without regard
to its conflict of laws principles, and the parties consent to venue in [VENUE].

IN WITNESS WHEREOF, the parties have executed this Agreement as of the Effective Date.


[COMPANY NAME]                            [WAREHOUSE NAME]


By: _______________________________       By: _______________________________

Name: _____________________________       Name: _____________________________

Title: ____________________________       Title: ____________________________

Date: _____________________________       Date: _____________________________


─────────────────────────────────────────────────────────────────────────────
SCHEDULE 1 — RATES AND CHARGES
─────────────────────────────────────────────────────────────────────────────

Every charge payable. A charge that is not written here is not payable.

Schedule date ............................... __________________________________
Rates fixed until ........................... __________________________________

STORAGE
Rate per unit ............................... __________________________________
Unit and how it is counted .................. __________________________________
Billing period and proration ................ __________________________________
Minimum monthly storage charge, if any ...... __________________________________

HANDLING IN
Trailer or container unload ................. __________________________________
Receiving and count, per pallet ............. __________________________________
Putaway, per pallet ......................... __________________________________
Repalletising or reconfiguration ............ __________________________________
Pallet in, and pallet purchase or exchange .. __________________________________

HANDLING OUT
Order pull and staging, per pallet .......... __________________________________
Loading, per pallet or per trailer .......... __________________________________
Pallet out .................................. __________________________________
Wrapping, labelling and documentation ....... __________________________________

OTHER
Cycle count beyond the agreed cadence ....... __________________________________
Rework, relabel or repack, per hour ......... __________________________________
Every accessorial charge, listed ............ __________________________________

Signed for Brand ............................ __________________________________

Signed for Operator ......................... __________________________________


─────────────────────────────────────────────────────────────────────────────
SCHEDULE 2 — SHRINKAGE ALLOWANCE, CONVERTED
─────────────────────────────────────────────────────────────────────────────

Do not agree a shrinkage percentage without completing this table.

Allowance, as stated in Section 9.1 ......... __________________________________
Base it is applied to ....................... __________________________________
Expected cases in the period ................ __________________________________
Allowance expressed in cases ................ __________________________________
Brand's landed cost per case ................ __________________________________
ALLOWANCE EXPRESSED IN DOLLARS .............. __________________________________
Date this conversion was calculated ......... __________________________________


─────────────────────────────────────────────────────────────────────────────
SCHEDULE 3 — GOODS AND HANDLING REQUIREMENTS
─────────────────────────────────────────────────────────────────────────────

For each Product: SKU, case pack, pallet configuration, tie and high, stacking limit, stated shelf
life, storage temperature and humidity requirement, rotation standard, minimum remaining shelf
life on release, and any special handling or freeze-protection requirement.
`,
  },
];

export default DISTRIBUTION_TEMPLATES;
